import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildImageGroupDisplayMessages } from '../../components/common/chat/composer/chatImageGroupUtils';
import useChatMentionTaskResolution from '../../components/common/chat/mentions/useChatMentionTaskResolution';
import useChatDragDrop from '../../components/common/chat/composer/useChatDragDrop';
import useChatProfileModalState from '../../components/common/chat/members/useChatProfileModalState';
import ChatMessageSelectableCopyArea from '../../components/common/chat/timeline/ChatMessageSelectableCopyArea';
import { makeLinkPreviewReadyHandler } from '../../components/common/chat/timeline/linkPreviewRealtime';
import useChatAnnouncements from '../../components/common/chat/announcements/useChatAnnouncements';
import useDelayedVisibility from '../../components/common/chat/shared/useDelayedVisibility';
import useChatMessageReactions from '../../components/common/chat/reactions/useChatMessageReactions';
import useChatMessageReadStatus from '../../components/common/chat/reactions/useChatMessageReadStatus';
import useChatNotesBadgeCount from '../../components/common/chat/notes/useChatNotesBadgeCount';
import useViewportBoundedMenuPosition from '../../components/common/chat/shared/useViewportBoundedMenuPosition';
import { focusChatAnchorMessage } from '../../components/common/chat/timeline/chatMessageAnchorUtils';
import { supportsChatMessageReaction } from '../../components/common/chat/reactions/chatMessageReactionUtils';
import {
  hasCopyableChatMessageText,
  resolveCopyableChatMessageText,
  writeTextToClipboard,
} from '../../components/common/chat/timeline/chatMessageCopyUtils';
import { buildToolbarFilterChips } from '../../components/common/chat/search/chatTargetSearchChipUtils';
import { useToast } from '../../components/common/ToastSystem';
import { useAuth } from '../../contexts/AuthContext';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { getSocket } from '../../lib/socket';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import { sendFile, sendImages } from '../../components/common/chat/shared/chatApi';
import {
  appendVariant,
} from '../../components/common/chat/composer/chatAssetUtils';
import { fetchAdminGroupMemberModalData } from '../../components/common/chat/members/memberModalApiUtils';
import { canUnsendGroupChatMessage } from '../../components/common/chat/timeline/groupChatMessageUtils';
import {
  patchMessageReadCountsByIds,
} from '../../components/common/chat/reactions/readReceiptRealtimeUtils';
import { mergeMessages, removeMessages } from '../../components/common/chat/timeline/timelineMerge';
import { shouldExcludeAdminSenderForMessage } from '../../components/common/chat/timeline/adminSenderExclusionUtils';
import { resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import { formatAdminDisplayName } from '../../utils/adminDisplayName';
import { listAdminUsers } from '../../api/adminAccounts';
import { listAdminArtists, listArtistGroups, listParts } from '../../api/artists';
import {
  listAdminPendingMentionTargets,
  resolveAdminMentionTarget,
} from '../../api/adminChat';
import { closeAdminGroupChatThread, createAdminGroupChatThread, deleteAdminGroupChatMessage, deleteAdminGroupChatThread, deleteAdminGroupChatThreadIcon, getAdminGroupChatNotifyMute, getAdminGroupChatMessagesAround, getAdminGroupChatThread, listAdminGroupChatThreads, listAdminGroupChatMessages, markAdminGroupChatMessagesRead, openAdminGroupChatThread, uploadAdminGroupChatThreadIcon, updateAdminGroupChatThreadSettings, updateAdminGroupChatNotifyMute } from '../../api/adminGroupChat';
import '../../css/admin/chats/ChatPage.css';
import '../../css/admin/chats/MemberListModal.css';
import '../../css/admin/chats/AdminGroupChatPage.css';
import '../../css/common/chat/ChatDragDrop.css';
import { clearActiveChatContext, setActiveChatContext } from '../../utils/activeChatContext';
import {
  CHAT_FOLLOWUP_AI_TARGET_ID,
  useAdminChatFollowupSummary,
} from '../../components/common/chat/followups/chatFollowupApi';
import { openAdminChatFollowupTarget } from '../../components/common/chat/followups/chatFollowupNavigation';
import { useConfirm } from '../../design-system/ConfirmDialog';

import { API_URL, buildAdminGroupNotesListUrl, MESSAGE_IMAGE_PATH, GROUP_CHAT_ASSIGNEE_SEARCH_STORAGE_KEY, buildAssetUrl, getThreadModeLabel, getThreadPreviewText, GROUP_CHAT_PAGE_SIZE, GROUP_CHAT_MAX_FILE_BYTES, wait, isImageFile, firstNonEmpty, normalizeRealtimePayload, pickRealtimeThreadId, buildRealtimeThreadLatestMessage, buildRealtimeThreadTextMessage } from '../../components/admin/chats/adminGroupChatPageHelpers';
import useAdminGroupChatMemberState from './useAdminGroupChatPageElement.members';
import AdminGroupChatPageView from './useAdminGroupChatPageElement.view';


export default function useAdminGroupChatPageElement() {
  const ACTIVE_CHAT_OWNER = 'admin:group-chat';
  const location = useLocation();
  const navigate = useNavigate();
  const { user, fetchSession, checking } = useAuth();
  const {
    adminArtistChatTone,
    adminMessageCount,
    markAdminGroupThreadAsRead,
  } = useMessageCount();
  const adminRole = String(user?.role || 'general').trim().toLowerCase() || 'general';
  const adminPermissions = Array.isArray(user?.permissions)
    ? user.permissions
    : user?.permissions
      ? [user.permissions]
      : [];
  const canViewGroupNotes = resolveAdminPermission({
    role: adminRole,
    isExternalAccount: user?.is_external_account === true,
    permissions: adminPermissions,
    permissionKey: 'GROUP_CHAT_VIEW_NOTES',
  });
  const canManageGroupNotes = resolveAdminPermission({
    role: adminRole,
    isExternalAccount: user?.is_external_account === true,
    permissions: adminPermissions,
    permissionKey: 'GROUP_CHAT_EDIT_NOTES',
  });
  const canManageAccounts = resolveAdminPermission({
    role: adminRole,
    isExternalAccount: user?.is_external_account === true,
    permissions: adminPermissions,
    permissionKey: 'SYSTEM_MANAGE_ACCOUNTS',
  });
  const canViewArtistDirectory =
    resolveAdminPermission({
      role: adminRole,
      isExternalAccount: user?.is_external_account === true,
      permissions: adminPermissions,
      permissionKey: 'ARTIST_VIEW_LIST',
    }) ||
    resolveAdminPermission({
      role: adminRole,
      isExternalAccount: user?.is_external_account === true,
      permissions: adminPermissions,
      permissionKey: 'NAV_ARTISTS',
    });
  const canUseGroupChat = resolveAdminPermission({
    role: adminRole,
    isExternalAccount: user?.is_external_account === true,
    permissions: adminPermissions,
    permissionKey: 'NAV_GROUP_CHAT',
  });
  const canUsePersonalChat = resolveAdminPermission({
    role: adminRole,
    isExternalAccount: user?.is_external_account === true,
    permissions: adminPermissions,
    permissionKey: 'NAV_PERSONAL_CHAT',
  });
  const canUseGroupChatForSession = useCallback(
    (sessionUser) =>
      resolveAdminPermission({
        role: sessionUser?.role || 'general',
        isExternalAccount: sessionUser?.is_external_account === true,
        permissions: Array.isArray(sessionUser?.permissions)
          ? sessionUser.permissions
          : sessionUser?.permissions
            ? [sessionUser.permissions]
            : [],
        permissionKey: 'NAV_GROUP_CHAT',
      }),
    []
  );
  const initialThreadIdFromQuery = useMemo(() => {
    try {
      return String(new URLSearchParams(location.search || '').get('thread') || '').trim();
    } catch {
      return '';
    }
  }, [location.search]);
  const initialAnchorMessageIdFromQuery = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return String(
        params.get('message') ||
          params.get('messageId') ||
          params.get('message_id') ||
          ''
      ).trim();
    } catch {
      return '';
    }
  }, [location.search]);
  const { success: toastSuccess, error: toastError } = useToast();
  const [threads, setThreads] = useState([]);
  const confirmDialog = useConfirm();
  const [selectedThreadId, setSelectedThreadId] = useState(() => initialThreadIdFromQuery);
  const [isAiTargetSelected, setIsAiTargetSelected] = useState(() => !initialThreadIdFromQuery);
  const [followupRefreshKey, setFollowupRefreshKey] = useState(0);
  const [selectedThread, setSelectedThread] = useState(null);
  const [pendingMentionTargets, setPendingMentionTargets] = useState({});
  const [resolvingMentionTargetId, setResolvingMentionTargetId] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [replyDraft, setReplyDraft] = useState(null);
  const refreshPendingMentionTargets = useCallback(async () => {
    if (!canUseGroupChat) return;
    try {
      const data = await listAdminPendingMentionTargets({ scope: 'group' });
      const next = {};
      (Array.isArray(data?.targets) ? data.targets : []).forEach((target) => {
        const id = String(target?.targetId || '').trim();
        if (!id) return;
        next[id] = {
          count: Number(target?.count || 0),
          messageId: target?.messageId || target?.message_id || null,
          latestCreatedAt: target?.latestCreatedAt || null,
        };
      });
      setPendingMentionTargets(next);
    } catch {
      setPendingMentionTargets({});
    }
  }, [canUseGroupChat]);
  const resolveMentionTarget = useCallback(async (targetId) => {
    const normalizedTargetId = String(targetId || '').trim();
    if (!normalizedTargetId) return;
    setResolvingMentionTargetId(normalizedTargetId);
    try {
      await resolveAdminMentionTarget({ scope: 'group', targetId: normalizedTargetId });
      setPendingMentionTargets((current) => {
        const next = { ...current };
        delete next[normalizedTargetId];
        return next;
      });
    } catch {
      toastError('メンション対応状態の更新に失敗しました。');
    } finally {
      setResolvingMentionTargetId('');
    }
  }, [toastError]);

  const updatePendingMentionTargetCount = useCallback((scope, targetId, delta) => {
    if (scope !== 'group') return;
    const normalizedTargetId = String(targetId || '').trim();
    if (!normalizedTargetId) return;
    setPendingMentionTargets((current) => {
      const currentTarget = current[normalizedTargetId];
      const nextCount = Math.max(0, Number(currentTarget?.count || 0) + Number(delta || 0));
      const next = { ...current };
      if (nextCount <= 0) {
        delete next[normalizedTargetId];
      } else {
        next[normalizedTargetId] = { ...currentTarget, count: nextCount };
      }
      return next;
    });
  }, []);

  const {
    modalProps: mentionResolutionModalProps,
    loadingTargetKey: loadingMentionResolutionTargetKey,
    openMentionResolution,
  } = useChatMentionTaskResolution({
    actorType: 'admin',
    onTargetCountChange: updatePendingMentionTargetCount,
    onError: toastError,
  });
  useEffect(() => {
    refreshPendingMentionTargets();
  }, [refreshPendingMentionTargets]);
  const { noteCount, refreshNoteCount } = useChatNotesBadgeCount({
    ownerId: selectedThreadId,
    buildListUrl: buildAdminGroupNotesListUrl,
    enabled: Boolean(selectedThreadId && !isAiTargetSelected && canViewGroupNotes),
  });
  useEffect(() => {
    if (!canViewGroupNotes && showNotes) {
      setShowNotes(false);
    }
  }, [canViewGroupNotes, showNotes]);
  useEffect(() => {
    setReplyDraft(null);
  }, [selectedThreadId]);
  const {
    announcements,
    latestAnnouncement,
    announcementCount,
    listOpen: isAnnouncementListOpen,
    setListOpen: setIsAnnouncementListOpen,
    refreshAnnouncements,
    pinAnnouncement,
    unpinAnnouncement,
    isAnnouncedMessage,
    actionMessageId: announcementActionMessageId,
  } = useChatAnnouncements({
    mode: 'admin',
    scope: 'group',
    threadId: selectedThreadId || '',
    enabled: Boolean(selectedThreadId && !isAiTargetSelected),
    onError: toastError,
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const {
    detailOpen: isReactionDetailOpen,
    detailGroups: reactionDetailGroups,
    detailLoading: reactionDetailLoading,
    openReactionDetail,
    closeReactionDetail,
    toggleReaction,
    refreshMessageReactionDetails,
    isReactionPending,
  } = useChatMessageReactions({
    mode: 'admin',
    scope: 'group',
    threadId: selectedThreadId || '',
    enabled: Boolean(selectedThreadId && !isAiTargetSelected),
    setMessages,
    onError: toastError,
  });
  const {
    detailOpen: isReadStatusDetailOpen,
    detailLoading: readStatusDetailLoading,
    detailReaders: readStatusDetailReaders,
    openReadStatusDetail,
    closeReadStatusDetail,
  } = useChatMessageReadStatus({
    scope: 'group',
    threadId: selectedThreadId || '',
    enabled: Boolean(selectedThreadId && !isAiTargetSelected),
    onError: toastError,
  });
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingThreadDetail, setLoadingThreadDetail] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadedMessagesThreadId, setLoadedMessagesThreadId] = useState('');
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [loadingNewerMessages, setLoadingNewerMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const showDelayedInitialLoading = useDelayedVisibility(
    loadingMessages && messages.length === 0,
    1000
  );
  const [messageWindowOffset, setMessageWindowOffset] = useState(0);
  const [activeAnchorMessageId, setActiveAnchorMessageId] = useState('');
  const [messageMenu, setMessageMenu] = useState(null);
  const [inlineCopyMessageId, setInlineCopyMessageId] = useState('');
  const [unsendingMessageId, setUnsendingMessageId] = useState('');
  const [uploadingDropFiles, setUploadingDropFiles] = useState(false);
  const [imgModalOpen, setImgModalOpen] = useState(false);
  const [imgModalIndex, setImgModalIndex] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createMode, setCreateMode] = useState('manual');
  const [createGroupId, setCreateGroupId] = useState('');
  const [showCreateArtistModal, setShowCreateArtistModal] = useState(false);
  const [createArtistKeyword, setCreateArtistKeyword] = useState('');
  const [createArtistAccountType, setCreateArtistAccountType] = useState('all');
  const [createArtistFilterGroupId, setCreateArtistFilterGroupId] = useState('');
  const [createArtistFilterPart, setCreateArtistFilterPart] = useState('');
  const [createArtistCandidates, setCreateArtistCandidates] = useState([]);
  const [loadingCreateArtistCandidates, setLoadingCreateArtistCandidates] = useState(false);
  const [createSelectedArtists, setCreateSelectedArtists] = useState([]);
  const [parts, setParts] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [artistGroups, setArtistGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [search, setSearch] = useState('');
  const [messageSearch, setMessageSearch] = useState('');
  const [modeSearch, setModeSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchDraft, setSearchDraft] = useState({
    thread: '',
    message: '',
    mode: '',
    status: '',
    groupId: '',
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsThread, setSettingsThread] = useState(null);
  const [settingsTitle, setSettingsTitle] = useState('');
  const [settingsAssigneeIds, setSettingsAssigneeIds] = useState([]);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [assigneeKeyword, setAssigneeKeyword] = useState('');
  const [assigneeDraftIds, setAssigneeDraftIds] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [deletingThread, setDeletingThread] = useState(false);
  const [threadMuted, setThreadMuted] = useState(true);
  const [loadingMute, setLoadingMute] = useState(false);
  const [savingMute, setSavingMute] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showMembersAddModal, setShowMembersAddModal] = useState(false);
  const [membersPayload, setMembersPayload] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [artistKeyword, setArtistKeyword] = useState('');
  const [memberAddAccountType, setMemberAddAccountType] = useState('all');
  const [memberAddFilterGroupId, setMemberAddFilterGroupId] = useState('');
  const [memberAddFilterPart, setMemberAddFilterPart] = useState('');
  const [artistCandidates, setArtistCandidates] = useState([]);
  const [loadingArtistCandidates, setLoadingArtistCandidates] = useState(false);
  const [candidateSelection, setCandidateSelection] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [removingArtistId, setRemovingArtistId] = useState('');
  const [showIconCropModal, setShowIconCropModal] = useState(false);
  const [iconCropImageSrc, setIconCropImageSrc] = useState('');
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [deletingIcon, setDeletingIcon] = useState(false);
  const {
    profileTarget,
    closeProfileModal,
    openAdminProfile,
    openArtistProfile,
    openProfileTarget,
  } = useChatProfileModalState();
  const settingsIconFileInputRef = useRef(null);
  const createArtistSearchTimerRef = useRef(null);
  const membersAddSearchTimerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatDropScopeRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const anchorHighlightTimerRef = useRef(null);
  const pendingAnchorRef = useRef(
    initialThreadIdFromQuery && initialAnchorMessageIdFromQuery
      ? { threadId: initialThreadIdFromQuery, messageId: initialAnchorMessageIdFromQuery }
      : null
  );
  const selectedThreadIdRef = useRef(initialThreadIdFromQuery);
  const hasSelectedThreadLoadEffectRunRef = useRef(false);
  const pendingReadThreadIdsRef = useRef(new Map());
  const isGroupChatAreaOpenRef = useRef(false);
  const settingsThreadIdRef = useRef('');
  const sidebarPressRef = useRef({
    suppressClick: false,
    suppressContextMenu: false,
    timer: null,
    contextMenuTimer: null,
  });
  const swipeStateRef = useRef(null);
  const isAiTargetSelectedRef = useRef(!initialThreadIdFromQuery);
  const prunePendingReadThreads = useCallback(() => {
    const now = Date.now();
    for (const [threadId, expiresAt] of pendingReadThreadIdsRef.current.entries()) {
      if (!Number.isFinite(expiresAt) || expiresAt <= now) {
        pendingReadThreadIdsRef.current.delete(threadId);
      }
    }
  }, []);
  const markThreadPendingRead = useCallback((threadId) => {
    const normalizedThreadId = String(threadId || '').trim();
    if (!normalizedThreadId) return;
    pendingReadThreadIdsRef.current.set(normalizedThreadId, Date.now() + 1500);
  }, []);
  const clearThreadPendingRead = useCallback((threadId) => {
    const normalizedThreadId = String(threadId || '').trim();
    if (!normalizedThreadId) return;
    pendingReadThreadIdsRef.current.delete(normalizedThreadId);
  }, []);
  const shouldSuppressThreadUnread = useCallback((threadId) => {
    const normalizedThreadId = String(threadId || '').trim();
    if (!normalizedThreadId) return false;
    prunePendingReadThreads();
    return pendingReadThreadIdsRef.current.has(normalizedThreadId);
  }, [prunePendingReadThreads]);
  const markThreadUnreadLocally = useCallback((threadId) => {
    const normalizedThreadId = String(threadId || '').trim();
    if (!normalizedThreadId) return;
    markThreadPendingRead(normalizedThreadId);
    setThreads((current) =>
      current.map((thread) =>
        String(thread?.thread_id || '') === normalizedThreadId
          ? { ...thread, unread_count: 0 }
          : thread,
      ),
    );
    markAdminGroupThreadAsRead(normalizedThreadId);
  }, [markAdminGroupThreadAsRead, markThreadPendingRead]);
  const appliedQueryThreadRef = useRef('');

  const initialMobileView = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const narrow = window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
    if (!narrow) return null;
    return initialThreadIdFromQuery ? 'chat' : 'list';
  }, [initialThreadIdFromQuery]);
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
  });
  const [mobileView, setMobileView] = useState(initialMobileView);
  const isGroupChatAreaOpen = Boolean(
    selectedThreadId &&
      !isAiTargetSelected &&
      !showNotes &&
      (!isNarrow || mobileView === 'chat'),
  );
  useEffect(() => {
    isGroupChatAreaOpenRef.current = isGroupChatAreaOpen;
  }, [isGroupChatAreaOpen]);

  useEffect(() => {
    if (isGroupChatAreaOpen) {
      setActiveChatContext({
        owner: ACTIVE_CHAT_OWNER,
        scope: 'admin',
        channel: 'group',
        threadId: selectedThreadId,
      });
      return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
    }
    clearActiveChatContext(ACTIVE_CHAT_OWNER);
    return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
  }, [isGroupChatAreaOpen, selectedThreadId]);

  const mobileListPrefRef = useRef(initialMobileView === 'list');
  const mobileChatHistoryRef = useRef(false);
  const {
    summary: followupSummary,
    badgeCount: followupBadgeCount,
  } = useAdminChatFollowupSummary();
  const loggedInAdminId = useMemo(() => {
    if (!user) return null;
    return user?.adminUserId || user?.userId || user?.id || null;
  }, [user]);

  const patchThreadTargetFromRealtime = useCallback((payload = {}) => {
    const threadId = pickRealtimeThreadId(payload);
    if (!threadId) return;
    clearThreadPendingRead(threadId);
    const latestMessage = buildRealtimeThreadLatestMessage(payload);
    const sourcePayload = normalizeRealtimePayload(payload);
    const senderType = String(
      sourcePayload.senderType ||
        sourcePayload.sender_type ||
        sourcePayload.message?.sender_type ||
        ''
    ).trim().toLowerCase();
    const senderId = firstNonEmpty(
      sourcePayload.senderId,
      sourcePayload.sender_id,
      sourcePayload.message?.admin_user_id,
      sourcePayload.message?.artist_user_id
    );
    const exactUnreadCount = Number(sourcePayload.unreadCount ?? sourcePayload.unread_count);
    const isSelfMessage =
      senderType === 'admin' &&
      loggedInAdminId != null &&
      senderId &&
      senderId === String(loggedInAdminId);
    setThreads((current) => {
      const index = current.findIndex((thread) => String(thread?.thread_id || '') === threadId);
      if (index < 0) return current;
      const thread = current[index];
      const nextUnread =
        Number.isFinite(exactUnreadCount) && exactUnreadCount >= 0
          ? Math.round(exactUnreadCount)
          : isSelfMessage
            ? Number(thread?.unread_count || 0)
            : Number(thread?.unread_count || 0) + 1;
      const patched = {
        ...thread,
        latest_message: latestMessage,
        latest_message_at: latestMessage.created_at,
        updated_at: latestMessage.created_at,
        unread_count: nextUnread,
      };
      return [patched, ...current.slice(0, index), ...current.slice(index + 1)];
    });
  }, [clearThreadPendingRead, loggedInAdminId]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const listOnly = isNarrow && mobileView === 'list';
    window.dispatchEvent(
      new CustomEvent('casticart:chat-footer', { detail: { listOnly, chatMode: 'group' } })
    );
  }, [isNarrow, mobileView]);

  const handleOpenPersonalChatFromTargetList = useCallback(() => {
    navigate('/admin/workspaces/communications/artist-chat', {
      replace: true,
      state: { reset: Date.now(), chatType: 'artist' },
    });
  }, [navigate]);

  const loadThreads = useCallback(async ({ messageKeyword, silent = false, attempt = 0 } = {}) => {
    if (checking) return;
    if (!canUseGroupChat) {
      setThreads([]);
      setSelectedThreadId('');
      return;
    }
    const effectiveMessageKeyword = String(
      messageKeyword != null ? messageKeyword : messageSearch
    ).trim();
    if (!silent) {
      setLoadingThreads(true);
    }
    try {
      const data = await listAdminGroupChatThreads(
        effectiveMessageKeyword ? { message: effectiveMessageKeyword } : undefined
      );
      const list = Array.isArray(data?.threads)
        ? data.threads.map((thread) => ({
            ...thread,
            admin_muted: thread?.admin_muted == null ? true : !!thread.admin_muted,
            unread_count: shouldSuppressThreadUnread(thread?.thread_id)
              ? 0
              : Number(thread?.unread_count || 0),
          }))
        : [];
      setThreads(list);
      setSelectedThreadId((current) => {
        if (current) return current;
        return '';
      });
    } catch (error) {
      if (Number(error?.status) === 403) {
        const refreshed = await fetchSession?.().catch(() => null);
        if (canUseGroupChatForSession(refreshed)) {
          if (attempt < 2) {
            await wait(300 * (attempt + 1));
            return loadThreads({ messageKeyword: effectiveMessageKeyword, silent, attempt: attempt + 1 });
          }
          return;
        }
      }
      if (!silent) {
        toastError(error?.message || 'グループチャット一覧の取得に失敗しました');
      }
      setThreads([]);
      setSelectedThreadId('');
    } finally {
      if (!silent) {
        setLoadingThreads(false);
      }
    }
  }, [
    canUseGroupChat,
    canUseGroupChatForSession,
    checking,
    fetchSession,
    messageSearch,
    shouldSuppressThreadUnread,
    toastError,
  ]);

  const loadThreadDetail = useCallback(
    async (threadId, { updateSelected = true } = {}) => {
      if (!threadId) {
        if (updateSelected) {
          setSelectedThread(null);
        }
        return null;
      }
      setLoadingThreadDetail(true);
      try {
        const data = await getAdminGroupChatThread(String(threadId));
        const nextThread = data?.thread
          ? {
              ...data.thread,
              admin_muted: data?.thread?.admin_muted == null ? true : !!data.thread.admin_muted,
            }
          : null;
        if (updateSelected) {
          setSelectedThread(nextThread);
        }
        return nextThread;
      } catch (error) {
        if (error?.transientPermissionSync) return null;
        toastError(error?.message || 'チャット詳細の取得に失敗しました');
        if (updateSelected) {
          setSelectedThread(null);
        }
        return null;
      } finally {
        setLoadingThreadDetail(false);
      }
    },
    [toastError]
  );

  const loadMessages = useCallback(
    async (threadId, { anchorMessageId = '', markRead = true, silent = false } = {}) => {
      if (!threadId) {
        setMessages([]);
        setLoadedMessagesThreadId('');
        setHasMoreMessages(false);
        setHasNewerMessages(false);
        setMessageWindowOffset(0);
        setActiveAnchorMessageId('');
        return [];
      }

      const normalizedAnchorMessageId = String(anchorMessageId || '').trim();
      setLoadedMessagesThreadId('');
      if (!silent) {
        setLoadingMessages(true);
      }
      try {
        const data = normalizedAnchorMessageId
          ? await getAdminGroupChatMessagesAround(String(threadId), normalizedAnchorMessageId, 25, 25)
          : await listAdminGroupChatMessages(String(threadId), 0, GROUP_CHAT_PAGE_SIZE);

        const rows = Array.isArray(data?.messages) ? data.messages : [];
        // wire は DESC(§1)。全流入を merge に通し DESC+一意をコードで保証する(§3.2)。
        setMessages(mergeMessages([], rows));
        if (normalizedAnchorMessageId) {
          setHasMoreMessages(Boolean(data?.hasOlder));
          setHasNewerMessages(Boolean(data?.hasNewer));
          setMessageWindowOffset(Number.isFinite(Number(data?.offset)) ? Number(data.offset) : 0);
          focusChatAnchorMessage({
            messageId: normalizedAnchorMessageId,
            containerRef: messagesContainerRef,
            setActiveAnchorMessageId,
            timerRef: anchorHighlightTimerRef,
            durationMs: 3500,
          });
        } else {
          setHasMoreMessages(Boolean(data?.hasMore) || rows.length >= GROUP_CHAT_PAGE_SIZE);
          setHasNewerMessages(false);
          setMessageWindowOffset(0);
          setActiveAnchorMessageId('');
        }

        // §3.6: ANCHOR ジャンプで開いた時は最新未到達のため既読化を保留。
        const shouldMarkRead = markRead && !normalizedAnchorMessageId;
        if (shouldMarkRead && rows[0]?.id) {
          await markAdminGroupChatMessagesRead({
            threadId,
            lastReadMessageId: rows[0].id,
          }).catch(() => {});
        }
        if (shouldMarkRead) {
          markThreadUnreadLocally(threadId);
        }
        return rows;
      } catch (error) {
        if (normalizedAnchorMessageId && Number(error?.status) === 404) {
          try {
            const fallbackData = await listAdminGroupChatMessages(
              String(threadId),
              0,
              GROUP_CHAT_PAGE_SIZE
            );
            const fallbackRows = Array.isArray(fallbackData?.messages) ? fallbackData.messages : [];
            setMessages(mergeMessages([], fallbackRows));
            setHasMoreMessages(
              Boolean(fallbackData?.hasMore) || fallbackRows.length >= GROUP_CHAT_PAGE_SIZE
            );
            setHasNewerMessages(false);
            setMessageWindowOffset(0);
            setActiveAnchorMessageId('');
            if (markRead && fallbackRows[0]?.id) {
              await markAdminGroupChatMessagesRead({
                threadId,
                lastReadMessageId: fallbackRows[0].id,
              }).catch(() => {});
            }
            if (markRead) {
              markThreadUnreadLocally(threadId);
            }
            toastError('対象メッセージが見つからないため、最新メッセージを表示しました');
            return fallbackRows;
          } catch (_) {}
        }
        if (silent) {
          return [];
        }
        toastError(error?.message || 'メッセージ取得に失敗しました');
        setMessages([]);
        setHasMoreMessages(false);
        setHasNewerMessages(false);
        setMessageWindowOffset(0);
        setActiveAnchorMessageId('');
        return [];
      } finally {
        if (String(selectedThreadIdRef.current || '').trim() === String(threadId || '').trim()) {
          setLoadedMessagesThreadId(String(threadId || '').trim());
        }
        if (!silent) {
          setLoadingMessages(false);
        }
      }
    },
    [markThreadUnreadLocally, toastError]
  );

  // NOTE(§4.1.2): 旧 scheduleRealtimeReconcile(700ms 後に最新ページ+スレッド詳細+一覧を
  // 再取得)は廃止。socket 受信は enriched=直接 merge / skeleton=最新ページ silent merge の
  // 2 経路に集約(§3.3 グループは newest 差分の代わりに最新ページを merge)。
  // スレッド一覧の鮮度は patchThreadTargetFromRealtime が担う(§3.7)。

  const loadOlderMessages = useCallback(async () => {
    if (!selectedThreadId || loadingMessages || loadingOlderMessages || !hasMoreMessages) return;
    setLoadingOlderMessages(true);
    try {
      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;
      const previousScrollTop = container?.scrollTop || 0;
      const isReverseColumn = container
        ? (getComputedStyle(container).flexDirection || '').toLowerCase().includes('column-reverse')
        : true;
      const currentCount = Array.isArray(messages) ? messages.length : 0;
      const nextOffset = messageWindowOffset + currentCount;
      const data = await listAdminGroupChatMessages(
        String(selectedThreadId),
        nextOffset,
        GROUP_CHAT_PAGE_SIZE
      );
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      setMessages((prev) => mergeMessages(prev, rows));
      setHasMoreMessages(Boolean(data?.hasMore) || rows.length >= GROUP_CHAT_PAGE_SIZE);
      requestAnimationFrame(() => {
        if (!messagesContainerRef.current) return;
        const nextScrollHeight = messagesContainerRef.current.scrollHeight;
        const heightDelta = nextScrollHeight - previousScrollHeight;
        messagesContainerRef.current.scrollTop = isReverseColumn
          ? previousScrollTop - heightDelta
          : previousScrollTop + heightDelta;
      });
    } catch (error) {
      toastError(error?.message || '過去メッセージの取得に失敗しました');
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [
    hasMoreMessages,
    loadingMessages,
    loadingOlderMessages,
    messageWindowOffset,
    messages,
    selectedThreadId,
    toastError,
  ]);

  const loadNewerMessages = useCallback(async () => {
    if (!selectedThreadId || loadingMessages || loadingNewerMessages || !hasNewerMessages) return;
    setLoadingNewerMessages(true);
    try {
      const previousScrollHeight = messagesContainerRef.current?.scrollHeight || 0;
      const nextOffset = Math.max(0, messageWindowOffset - GROUP_CHAT_PAGE_SIZE);
      const fetchLimit = Math.max(1, messageWindowOffset - nextOffset);
      const data = await listAdminGroupChatMessages(
        String(selectedThreadId),
        nextOffset,
        fetchLimit
      );
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      setMessages((prev) => mergeMessages(prev, rows));
      setMessageWindowOffset(nextOffset);
      setHasNewerMessages(nextOffset > 0);
      // §3.6: offset 0 に達したら LIVE 復帰 → 最新メッセージで既読化(ANCHOR からの収束)。
      if (nextOffset === 0 && isGroupChatAreaOpenRef.current && rows[0]?.id) {
        markAdminGroupChatMessagesRead({ threadId: selectedThreadId, lastReadMessageId: rows[0].id })
          .then(() => markThreadUnreadLocally(selectedThreadId))
          .catch(() => {});
      }
      requestAnimationFrame(() => {
        if (!messagesContainerRef.current) return;
        const nextScrollHeight = messagesContainerRef.current.scrollHeight;
        messagesContainerRef.current.scrollTop = Math.max(
          0,
          nextScrollHeight - previousScrollHeight + messagesContainerRef.current.scrollTop
        );
      });
    } catch (error) {
      toastError(error?.message || '新しいメッセージの取得に失敗しました');
    } finally {
      setLoadingNewerMessages(false);
    }
  }, [
    hasNewerMessages,
    loadingMessages,
    loadingNewerMessages,
    markThreadUnreadLocally,
    messageWindowOffset,
    selectedThreadId,
    toastError,
  ]);

  const isViewingLatestMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const direction = (getComputedStyle(container).flexDirection || '').toLowerCase();
    const nearBottomNormal =
      container.scrollHeight - container.clientHeight - container.scrollTop <= 80;
    const nearBottomReverse = Math.abs(container.scrollTop) <= 80;
    return direction.includes('column-reverse') ? nearBottomReverse : nearBottomNormal;
  }, []);

  const isViewingOldestMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false;
    const direction = (getComputedStyle(container).flexDirection || '').toLowerCase();
    if (direction.includes('column-reverse')) {
      return container.scrollHeight - container.clientHeight + container.scrollTop <= 80;
    }
    return container.scrollTop <= 80;
  }, []);

  const handleMessagesScroll = useCallback(() => {
    if (
      hasMoreMessages &&
      !loadingMessages &&
      !loadingOlderMessages &&
      isViewingOldestMessages()
    ) {
      loadOlderMessages();
    }
    if (
      hasNewerMessages &&
      !loadingMessages &&
      !loadingNewerMessages &&
      isViewingLatestMessages()
    ) {
      // ANCHOR 窓から最新(offset 0)へ到達すると hasNewerMessages=false になり LIVE 復帰。
      // その時点の loadNewerMessages 内で既読化する(§3.6)。ここでは連打防止のため
      // スクロール毎の既読 API は呼ばない。
      loadNewerMessages();
    }
  }, [
    hasMoreMessages,
    hasNewerMessages,
    isViewingOldestMessages,
    isViewingLatestMessages,
    loadOlderMessages,
    loadNewerMessages,
    loadingMessages,
    loadingOlderMessages,
    loadingNewerMessages,
  ]);

  useEffect(() => {
    if (
      hasMoreMessages &&
      !loadingMessages &&
      !loadingOlderMessages &&
      isViewingOldestMessages()
    ) {
      loadOlderMessages();
    }
  }, [
    hasMoreMessages,
    isViewingOldestMessages,
    loadOlderMessages,
    loadingMessages,
    loadingOlderMessages,
    messages.length,
  ]);

  useEffect(() => {
    const target = messagesContainerRef.current;
    if (!target) return undefined;
    target.addEventListener('scroll', handleMessagesScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleMessagesScroll);
  }, [handleMessagesScroll]);

  const loadArtistGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const groups = await listArtistGroups();
      setArtistGroups(groups);
    } catch (error) {
      setArtistGroups([]);
      toastError(error?.message || 'アーティストグループ一覧の取得に失敗しました');
    } finally {
      setLoadingGroups(false);
    }
  }, [toastError]);

  const loadParts = useCallback(async () => {
    setLoadingParts(true);
    try {
      const rows = await listParts();
      setParts(rows);
    } catch (error) {
      setParts([]);
      toastError(error?.message || 'パート一覧の取得に失敗しました');
    } finally {
      setLoadingParts(false);
    }
  }, [toastError]);

  const loadAdminUsers = useCallback(async () => {
    if (!canManageAccounts) {
      setAdminUsers([]);
      setLoadingAdminUsers(false);
      return;
    }
    setLoadingAdminUsers(true);
    try {
      const users = await listAdminUsers();
      setAdminUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      setAdminUsers([]);
      toastError(error?.message || '管理者一覧の取得に失敗しました');
    } finally {
      setLoadingAdminUsers(false);
    }
  }, [canManageAccounts, toastError]);

  const applyThreadMutedState = useCallback((threadId, muted) => {
    if (!threadId) return;
    const nextMuted = !!muted;
    setThreads((current) =>
      current.map((thread) =>
        String(thread?.thread_id || '') === String(threadId)
          ? { ...thread, admin_muted: nextMuted }
          : thread
      )
    );
    setSelectedThread((current) =>
      current && String(current?.thread_id || '') === String(threadId)
        ? { ...current, admin_muted: nextMuted }
        : current
    );
    setSettingsThread((current) =>
      current && String(current?.thread_id || '') === String(threadId)
        ? { ...current, admin_muted: nextMuted }
        : current
    );
  }, []);

  const loadMuteSetting = useCallback(
    async (threadId) => {
      if (!threadId) return;
      setLoadingMute(true);
      try {
        const data = await getAdminGroupChatNotifyMute(String(threadId));
        const muted = Boolean(data?.muted);
        setThreadMuted(muted);
        applyThreadMutedState(threadId, muted);
      } catch (error) {
        setThreadMuted(true);
        toastError(error?.message || '通知設定の取得に失敗しました');
      } finally {
        setLoadingMute(false);
      }
    },
    [applyThreadMutedState, toastError]
  );

  const loadMembers = useCallback(
    async (threadId) => {
      if (!threadId) {
        setMembersPayload(null);
        return;
      }
      setLoadingMembers(true);
      try {
        const { thread } = await fetchAdminGroupMemberModalData({
          apiUrl: API_URL,
          groupThreadId: threadId,
          partsList: parts,
        });
        if (!thread) {
          throw new Error('メンバー一覧の取得に失敗しました');
        }
        setMembersPayload(thread);
      } catch (error) {
        setMembersPayload(null);
        toastError(error?.message || 'メンバー一覧の取得に失敗しました');
      } finally {
        setLoadingMembers(false);
      }
    },
    [parts, toastError]
  );

  const searchArtistsForMemberAdd = useCallback(
    async (keyword) => {
      if (!canViewArtistDirectory) {
        setArtistCandidates([]);
        setLoadingArtistCandidates(false);
        return;
      }
      if (!selectedThreadId) return;
      setLoadingArtistCandidates(true);
      try {
        const query = {
          page: 1,
          limit: 200,
          sort: 'login',
          preReg: 'hide',
        };
        if (keyword) query.name = keyword;
        if (memberAddAccountType === 'solo' || memberAddAccountType === 'group') {
          query.accountType = memberAddAccountType;
        }
        if (memberAddFilterGroupId) {
          query.groupId = memberAddFilterGroupId;
        }
        if (memberAddFilterPart) {
          query.parts = memberAddFilterPart;
        }
        const data = await listAdminArtists(query);
        const rows = Array.isArray(data?.artists) ? data.artists : [];
        setArtistCandidates(rows);
      } catch (error) {
        setArtistCandidates([]);
        toastError(error?.message || '追加候補アーティストの取得に失敗しました');
      } finally {
        setLoadingArtistCandidates(false);
      }
    },
    [
      memberAddAccountType,
      memberAddFilterGroupId,
      memberAddFilterPart,
      canViewArtistDirectory,
      selectedThreadId,
      toastError,
    ]
  );

  const searchArtistsForCreate = useCallback(
    async (keyword) => {
      if (!canViewArtistDirectory) {
        setCreateArtistCandidates([]);
        setLoadingCreateArtistCandidates(false);
        return;
      }
      setLoadingCreateArtistCandidates(true);
      try {
        const query = {
          page: 1,
          limit: 200,
          sort: 'login',
          preReg: 'hide',
        };
        if (keyword) query.name = keyword;
        if (createArtistAccountType === 'solo' || createArtistAccountType === 'group') {
          query.accountType = createArtistAccountType;
        }
        if (createArtistFilterGroupId) {
          query.groupId = createArtistFilterGroupId;
        }
        if (createArtistFilterPart) {
          query.parts = createArtistFilterPart;
        }
        const data = await listAdminArtists(query);
        setCreateArtistCandidates(Array.isArray(data?.artists) ? data.artists : []);
      } catch (error) {
        setCreateArtistCandidates([]);
        toastError(error?.message || 'アーティスト候補の取得に失敗しました');
      } finally {
        setLoadingCreateArtistCandidates(false);
      }
    },
    [
      canViewArtistDirectory,
      createArtistAccountType,
      createArtistFilterGroupId,
      createArtistFilterPart,
      toastError,
    ]
  );

  useEffect(() => {
    if (checking) return;
    loadThreads();
  }, [checking, loadThreads]);

  useEffect(() => {
    isAiTargetSelectedRef.current = isAiTargetSelected;
  }, [isAiTargetSelected]);

  useEffect(() => {
    setInlineCopyMessageId('');
  }, [selectedThreadId]);

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  const shouldShowSidePanel = !isAiTargetSelected && Boolean(selectedThread);
  const isSidePanelLayoutOpen = shouldShowSidePanel && isSidePanelOpen;

  useEffect(() => {
    setShowNotes(false);
  }, [selectedThreadId]);

  useEffect(() => {
    closeProfileModal();
  }, [closeProfileModal, selectedThreadId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mm = window.matchMedia('(max-width: 700px)');
    const handler = (event) => {
      const narrow = event.matches;
      setIsNarrow(narrow);
      if (!narrow) {
        setMobileView(null);
        mobileListPrefRef.current = false;
        mobileChatHistoryRef.current = false;
        return;
      }
      if (mobileListPrefRef.current) {
        setMobileView('list');
        return;
      }
      setMobileView(selectedThreadIdRef.current || isAiTargetSelectedRef.current ? 'chat' : 'list');
    };
    handler(mm);
    mm.addEventListener ? mm.addEventListener('change', handler) : mm.addListener(handler);
    return () => {
      mm.removeEventListener ? mm.removeEventListener('change', handler) : mm.removeListener(handler);
    };
  }, []);

  const goBackMobile = useCallback(() => {
    mobileListPrefRef.current = true;
    mobileChatHistoryRef.current = false;
    setMobileView('list');
  }, []);

  const openAiInbox = useCallback(() => {
    setFollowupRefreshKey((current) => current + 1);
    setIsAiTargetSelected(true);
    if (!isNarrow) return;
    mobileListPrefRef.current = false;
    if (!mobileChatHistoryRef.current) {
      try {
        window.history.pushState(
          { mobileGroupChat: true, threadId: CHAT_FOLLOWUP_AI_TARGET_ID },
          '',
          '',
        );
        mobileChatHistoryRef.current = true;
      } catch {
        mobileChatHistoryRef.current = false;
      }
    }
    setMobileView('chat');
  }, [isNarrow]);

  const handleOpenAiTask = useCallback(
    (task) => {
      return openAdminChatFollowupTarget({
        task,
        currentChannel: 'group',
        navigate,
        openGroup: ({ threadId, messageId }) => {
          if (!threadId) return;
          const isSameThread = String(selectedThreadIdRef.current || '') === String(threadId);
          if (messageId && !isSameThread) {
            pendingAnchorRef.current = { threadId, messageId };
          }
          setIsAiTargetSelected(false);
          if (isSameThread) {
            pendingAnchorRef.current = null;
            loadThreadDetail(threadId);
            loadMessages(threadId, {
              anchorMessageId: messageId,
              markRead: isGroupChatAreaOpenRef.current,
            });
          } else {
            setSelectedThreadId(threadId);
          }
          if (isNarrow) {
            mobileListPrefRef.current = false;
            setMobileView('chat');
          }
        },
      });
    },
    [isNarrow, loadMessages, loadThreadDetail, navigate],
  );

  useEffect(() => {
    const onPop = () => {
      if (!isNarrow) return;
      mobileListPrefRef.current = true;
      mobileChatHistoryRef.current = false;
      setMobileView('list');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isNarrow]);

  useEffect(() => {
    if (!isNarrow) return;

    if (!selectedThreadId && !isAiTargetSelectedRef.current) {
      if (mobileView !== 'list') setMobileView('list');
      return;
    }

    if (mobileView === 'chat' && mobileListPrefRef.current) {
      mobileListPrefRef.current = false;
      return;
    }

    if (mobileListPrefRef.current) {
      if (mobileView !== 'list') setMobileView('list');
    } else if (mobileView !== 'chat') {
      setMobileView('chat');
    }
  }, [isNarrow, mobileView, selectedThreadId, isAiTargetSelected]);

  useEffect(() => {
    if (!initialThreadIdFromQuery) {
      appliedQueryThreadRef.current = '';
      return;
    }
    const queryKey = `${initialThreadIdFromQuery}:${initialAnchorMessageIdFromQuery}`;
    if (appliedQueryThreadRef.current === queryKey) return;
    appliedQueryThreadRef.current = queryKey;
    const isSameThread =
      String(selectedThreadIdRef.current || '') === String(initialThreadIdFromQuery || '');
    const shouldLoadSameThreadImmediately =
      isSameThread && hasSelectedThreadLoadEffectRunRef.current;
    if (initialAnchorMessageIdFromQuery && !shouldLoadSameThreadImmediately) {
      pendingAnchorRef.current = {
        threadId: initialThreadIdFromQuery,
        messageId: initialAnchorMessageIdFromQuery,
      };
    }
    setIsAiTargetSelected(false);
    setSelectedThreadId(initialThreadIdFromQuery);
    if (shouldLoadSameThreadImmediately) {
      pendingAnchorRef.current = null;
      loadThreadDetail(initialThreadIdFromQuery);
      loadMessages(initialThreadIdFromQuery, {
        anchorMessageId: initialAnchorMessageIdFromQuery,
        markRead: isGroupChatAreaOpenRef.current,
      });
    }
    if (isNarrow) {
      mobileListPrefRef.current = false;
      setMobileView('chat');
    }
    navigate('/admin/workspaces/communications/group-chat', { replace: true });
  }, [
    initialThreadIdFromQuery,
    initialAnchorMessageIdFromQuery,
    isNarrow,
    loadMessages,
    loadThreadDetail,
    navigate,
  ]);

  useEffect(() => {
    if (!isNarrow) {
      mobileChatHistoryRef.current = false;
      return;
    }
    if (
      mobileView === 'chat' &&
      (selectedThreadIdRef.current || isAiTargetSelectedRef.current) &&
      !mobileListPrefRef.current &&
      !mobileChatHistoryRef.current
    ) {
      try {
        window.history.pushState(
          { mobileGroupChat: true, threadId: selectedThreadIdRef.current || CHAT_FOLLOWUP_AI_TARGET_ID },
          '',
          '',
        );
        mobileChatHistoryRef.current = true;
      } catch {
        mobileChatHistoryRef.current = false;
      }
    }
  }, [isNarrow, mobileView, selectedThreadId, isAiTargetSelected]);

  useEffect(() => {
    hasSelectedThreadLoadEffectRunRef.current = true;
    const pending = pendingAnchorRef.current;
    const anchorMessageId =
      pending && String(pending.threadId || '') === String(selectedThreadId || '')
        ? String(pending.messageId || '')
        : '';
    pendingAnchorRef.current = null;
    loadThreadDetail(selectedThreadId);
    loadMessages(selectedThreadId, {
      anchorMessageId,
      markRead: isGroupChatAreaOpenRef.current,
    });
  }, [selectedThreadId, isGroupChatAreaOpen, loadThreadDetail, loadMessages]);

  useEffect(() => {
    const area = chatDropScopeRef.current;
    if (!area) return undefined;

    const handleTouchStart = (event) => {
      if (!isNarrow || mobileView !== 'chat') {
        swipeStateRef.current = null;
        return;
      }
      const touch = event.touches?.[0];
      if (!touch) return;
      swipeStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        locking: null,
        triggered: false,
      };
    };

    const handleTouchMove = (event) => {
      const state = swipeStateRef.current;
      if (!state || state.triggered || !isNarrow || mobileView !== 'chat') return;
      const touch = event.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;

      if (!state.locking) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          state.locking = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        } else {
          return;
        }
      }
      if (state.locking !== 'x') return;

      if (dx <= -80 && Math.abs(dy) < 60) {
        state.triggered = true;
        goBackMobile();
      }
    };

    const clearSwipe = () => {
      swipeStateRef.current = null;
    };

    area.addEventListener('touchstart', handleTouchStart, { passive: true });
    area.addEventListener('touchmove', handleTouchMove, { passive: true });
    area.addEventListener('touchend', clearSwipe);
    area.addEventListener('touchcancel', clearSwipe);
    return () => {
      area.removeEventListener('touchstart', handleTouchStart);
      area.removeEventListener('touchmove', handleTouchMove);
      area.removeEventListener('touchend', clearSwipe);
      area.removeEventListener('touchcancel', clearSwipe);
    };
  }, [goBackMobile, isNarrow, mobileView]);

  useEffect(() => {
    setMessageMenu(null);
    setUnsendingMessageId('');
    setImgModalOpen(false);
    setImgModalIndex(0);
  }, [selectedThreadId]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket.connected) {
      try {
        socket.connect();
      } catch (_) {}
    }

    const patchThreadList = (payload = {}) => {
      const payloadThreadId = pickRealtimeThreadId(payload);
      if (payloadThreadId) {
        refreshPendingMentionTargets();
      }
      if (
        payloadThreadId &&
        payloadThreadId === String(selectedThreadId || '').trim() &&
        isGroupChatAreaOpenRef.current
      ) {
        return;
      }
      if (payloadThreadId) {
        patchThreadTargetFromRealtime(payload);
      }
    };
    const patchThreadListRead = (payload = {}) => {
      const payloadThreadId = pickRealtimeThreadId(payload);
      if (!payloadThreadId) return;
      const readerType = String(payload?.readerType || payload?.reader_type || '').trim().toLowerCase();
      const readerId = String(payload?.readerId || payload?.reader_id || '').trim();
      if (
        readerType !== 'admin' ||
        loggedInAdminId == null ||
        !readerId ||
        readerId !== String(loggedInAdminId)
      ) {
        return;
      }
      setThreads((current) =>
        current.map((thread) =>
          String(thread?.thread_id || '') === payloadThreadId
            ? { ...thread, unread_count: 0 }
            : thread
        )
      );
      markAdminGroupThreadAsRead(payloadThreadId);
    };

    socket.on(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, patchThreadList);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, patchThreadListRead);

    return () => {
      socket.off(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, patchThreadList);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, patchThreadListRead);
    };
  }, [
    loggedInAdminId,
    markAdminGroupThreadAsRead,
    patchThreadTargetFromRealtime,
    refreshPendingMentionTargets,
    selectedThreadId,
  ]);

  useEffect(() => {
    if (!selectedThreadId || isAiTargetSelected) return undefined;
    const socket = getSocket();
    if (!socket.connected) {
      try {
        socket.connect();
      } catch (_) {}
    }

    socket.emit(SOCKET_EVENTS.JOIN_GROUP_CHAT_THREAD, selectedThreadId);

    // §3.3 RECONNECT: 再接続時に開いているグループスレッドを最新へ再同期(silent)。
    // onNewMessage のスケルトン経路(最新ページを silent fetch して merge)と同じ既存ロジックを
    // 再利用する。グループチャットが開いていなければ何もしない。
    const handleConnect = () => {
      if (!isGroupChatAreaOpenRef.current) return;
      (async () => {
        try {
          const data = await listAdminGroupChatMessages(String(selectedThreadId), 0, GROUP_CHAT_PAGE_SIZE);
          if (String(selectedThreadIdRef.current || '') !== String(selectedThreadId)) return;
          const rows = Array.isArray(data?.messages) ? data.messages : [];
          if (rows.length) setMessages((prev) => mergeMessages(prev, rows));
        } catch (_) {}
      })();
    };

    const onNewMessage = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      if (!isGroupChatAreaOpenRef.current) {
        return;
      }
      const realtimeMessage = buildRealtimeThreadTextMessage(payload);
      if (realtimeMessage) {
        // 経路①: enriched payload の直接 merge(read_count は timelineMerge が max 保持)。§3.3
        setMessages((prev) => mergeMessages(prev, [realtimeMessage]));
        markAdminGroupChatMessagesRead({
          threadId: selectedThreadId,
          lastReadMessageId: realtimeMessage.id,
        }).catch(() => {});
        markThreadUnreadLocally(selectedThreadId);
        return;
      }
      // 経路②: スケルトン payload → 最新ページを silent fetch して merge(§3.3 グループ)。
      // 旧実装は loadMessages で全置換していたが、window/スクロールを壊すため merge に変更。
      (async () => {
        try {
          const data = await listAdminGroupChatMessages(String(selectedThreadId), 0, GROUP_CHAT_PAGE_SIZE);
          if (String(selectedThreadIdRef.current || '') !== String(selectedThreadId)) return;
          const rows = Array.isArray(data?.messages) ? data.messages : [];
          if (rows.length) setMessages((prev) => mergeMessages(prev, rows));
        } catch (_) {}
      })();
      loadThreadDetail(selectedThreadId);
    };
    const onReadUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      const readMessageIds = Array.isArray(payload?.readMessageIds)
        ? payload.readMessageIds
        : payload?.read_message_ids;
      if (Array.isArray(readMessageIds) && readMessageIds.length > 0) {
        setMessages((prev) => patchMessageReadCountsByIds(prev, readMessageIds, payload, { scope: 'group' }));
        return;
      }
      loadMessages(selectedThreadId);
    };
    const onSettingsUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      loadThreadDetail(selectedThreadId);
      if (showMembersModal) {
        loadMembers(selectedThreadId);
      }
    };
    const onMembershipChanged = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      loadThreadDetail(selectedThreadId);
      if (showMembersModal) {
        loadMembers(selectedThreadId);
      }
    };
    const onMessageDeleted = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      const deletedIds = Array.isArray(payload?.messageIds) && payload.messageIds.length
        ? payload.messageIds.map((id) => String(id))
        : (payload?.messageId ? [String(payload.messageId)] : []);
      // §3.3 SOCKET_DEL: id 指定の remove のみ。スクロール位置に影響を与えない
      // (旧実装の loadMessages 全置換は窓/スクロールを壊すため撤去)。
      if (deletedIds.length > 0) {
        setMessages((prev) => removeMessages(prev, deletedIds));
      }
      loadThreadDetail(selectedThreadId);
    };
    const onAnnouncementsUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      refreshAnnouncements({ silent: true }).catch(() => {});
    };
    const onReactionUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(selectedThreadId)) return;
      if (!payload?.messageId) return;
      refreshMessageReactionDetails(payload.messageId).catch(() => {});
    };

    const handleLinkPreviewReady = makeLinkPreviewReadyHandler(setMessages);

    socket.on('connect', handleConnect);
    socket.on(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, onNewMessage);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, onReadUpdated);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_SETTINGS_UPDATED, onSettingsUpdated);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_MEMBERSHIP_CHANGED, onMembershipChanged);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_DELETED, onMessageDeleted);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_ANNOUNCEMENTS_UPDATED, onAnnouncementsUpdated);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_REACTION_UPDATED, onReactionUpdated);
    socket.on(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);

    return () => {
      socket.emit(SOCKET_EVENTS.LEAVE_GROUP_CHAT_THREAD, selectedThreadId);
      socket.off('connect', handleConnect);
      socket.off(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, onNewMessage);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, onReadUpdated);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_SETTINGS_UPDATED, onSettingsUpdated);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_MEMBERSHIP_CHANGED, onMembershipChanged);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_DELETED, onMessageDeleted);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_ANNOUNCEMENTS_UPDATED, onAnnouncementsUpdated);
      socket.off(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_REACTION_UPDATED, onReactionUpdated);
    };
  }, [
    loadMembers,
    loadMessages,
    loadThreadDetail,
    loggedInAdminId,
    markThreadUnreadLocally,
    refreshAnnouncements,
    refreshMessageReactionDetails,
    selectedThreadId,
    showMembersModal,
    isAiTargetSelected,
  ]);

  useEffect(() => {
    if (!createOpen || createMode !== 'artist_group_linked') return;
    if (artistGroups.length > 0 || loadingGroups) return;
    loadArtistGroups();
  }, [artistGroups.length, createMode, createOpen, loadArtistGroups, loadingGroups]);

  useEffect(() => {
    const needsArtistGroupLookup =
      (showCreateArtistModal && createMode === 'manual') ||
      (showMembersAddModal && membersPayload?.mode === 'manual');
    const needsSidePanelPartLookup =
      Array.isArray(selectedThread?.artists) && selectedThread.artists.length > 0;
    const needsPartLookup = showMembersModal || needsArtistGroupLookup || needsSidePanelPartLookup;
    if (needsArtistGroupLookup && !loadingGroups && artistGroups.length === 0) {
      loadArtistGroups();
    }
    if (needsPartLookup && !loadingParts && parts.length === 0) {
      loadParts();
    }
  }, [
    artistGroups.length,
    createMode,
    loadArtistGroups,
    loadParts,
    loadingGroups,
    loadingParts,
    membersPayload?.mode,
    parts.length,
    selectedThread?.artists,
    showMembersModal,
    showCreateArtistModal,
    showMembersAddModal,
  ]);

  useEffect(() => {
    if (createOpen) return;
    setShowCreateArtistModal(false);
    setCreateArtistKeyword('');
    setCreateArtistAccountType('all');
    setCreateArtistFilterGroupId('');
    setCreateArtistFilterPart('');
    setCreateArtistCandidates([]);
  }, [createOpen]);

  useEffect(() => {
    if (!showSettingsModal || !settingsThread) return;
    setSettingsTitle(String(settingsThread.title || ''));
    const nextAssigneeIds = Array.isArray(settingsThread.assignees)
      ? settingsThread.assignees
          .map((row) => Number.parseInt(row?.id, 10))
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];
    setSettingsAssigneeIds(nextAssigneeIds);
    setAssigneeDraftIds(nextAssigneeIds);
  }, [settingsThread, showSettingsModal]);

  useEffect(() => {
    if (!showSettingsModal || !selectedThread) return;
    const targetThreadId = String(settingsThreadIdRef.current || '').trim();
    if (!targetThreadId) return;
    if (String(selectedThread?.thread_id || '') !== targetThreadId) return;
    setSettingsThread(selectedThread);
  }, [selectedThread, showSettingsModal]);

  useEffect(() => {
    if (!showMembersAddModal || membersPayload?.mode !== 'manual') return;
    const timer = setTimeout(() => {
      searchArtistsForMemberAdd(String(artistKeyword || '').trim());
    }, 250);
    membersAddSearchTimerRef.current = timer;
    return () => clearTimeout(timer);
  }, [
    artistKeyword,
    membersPayload?.mode,
    searchArtistsForMemberAdd,
    showMembersAddModal,
  ]);

  useEffect(() => {
    if (!showCreateArtistModal || createMode !== 'manual') return;
    const timer = setTimeout(() => {
      searchArtistsForCreate(String(createArtistKeyword || '').trim());
    }, 250);
    createArtistSearchTimerRef.current = timer;
    return () => clearTimeout(timer);
  }, [createArtistKeyword, createMode, searchArtistsForCreate, showCreateArtistModal]);

  useEffect(
    () => () => {
      if (membersAddSearchTimerRef.current) {
        clearTimeout(membersAddSearchTimerRef.current);
        membersAddSearchTimerRef.current = null;
      }
      if (createArtistSearchTimerRef.current) {
        clearTimeout(createArtistSearchTimerRef.current);
        createArtistSearchTimerRef.current = null;
      }
      if (anchorHighlightTimerRef.current) {
        clearTimeout(anchorHighlightTimerRef.current);
        anchorHighlightTimerRef.current = null;
      }
      if (sidebarPressRef.current.timer) {
        clearTimeout(sidebarPressRef.current.timer);
        sidebarPressRef.current.timer = null;
      }
      if (sidebarPressRef.current.contextMenuTimer) {
        clearTimeout(sidebarPressRef.current.contextMenuTimer);
        sidebarPressRef.current.contextMenuTimer = null;
      }
    },
    []
  );

  const filteredThreads = useMemo(() => {
    const keyword = String(search || '').trim().toLowerCase();
    if (!keyword && !messageSearch && !modeSearch && !statusSearch && !groupSearch && !assigneeSearch) return threads;
    return threads.filter((thread) => {
      const title = String(thread?.title || '').toLowerCase();
      const modeLabel = getThreadModeLabel(thread?.mode).toLowerCase();
      const groupName = String(thread?.linked_artist_group_name || '').toLowerCase();
      const eventName = String(thread?.linked_event_name || '').toLowerCase();
      const preview = getThreadPreviewText(thread).toLowerCase();
      const matchedMode = !modeSearch || String(thread?.mode || '') === String(modeSearch);
      const matchedStatus = !statusSearch || String(thread?.status || '') === String(statusSearch);
      const matchedGroup =
        !groupSearch ||
        String(thread?.linked_artist_group_id || '') === String(groupSearch);
      const matchedAssignee = !assigneeSearch || (assigneeSearch === 'mine' ? Boolean(thread?.is_assigned_to_me) : true);
      const matchedThread =
        !keyword ||
        title.includes(keyword) ||
        modeLabel.includes(keyword) ||
        groupName.includes(keyword) ||
        eventName.includes(keyword) ||
        preview.includes(keyword);
      return matchedMode && matchedStatus && matchedGroup && matchedAssignee && matchedThread;
    });
  }, [assigneeSearch, groupSearch, messageSearch, modeSearch, search, statusSearch, threads]);

  const activeSearchFilterCount = useMemo(
    () =>
      [
        String(search || '').trim(),
        String(messageSearch || '').trim(),
        String(modeSearch || '').trim(),
        String(statusSearch || '').trim(),
        String(groupSearch || '').trim(),
        String(assigneeSearch || '').trim(),
      ].filter(Boolean).length,
    [assigneeSearch, groupSearch, messageSearch, modeSearch, search, statusSearch]
  );
  const searchSummaryLabel =
    activeSearchFilterCount > 0
      ? `絞り込み中: ${activeSearchFilterCount}件`
      : '';
  const shouldShowThreadSkeleton = loadingThreads && threads.length === 0 && activeSearchFilterCount === 0;
  const applyToolbarSearchFilters = useCallback(async ({
    thread = search,
    message = messageSearch,
    mode = modeSearch,
    status = statusSearch,
    groupId = groupSearch,
    assignee = assigneeSearch,
  } = {}) => {
    const nextThreadSearch = String(thread || '').trim();
    const nextMessageSearch = String(message || '').trim();
    const nextModeSearch = String(mode || '').trim();
    const nextStatusSearch = String(status || '').trim();
    const nextGroupSearch = String(groupId || '').trim();
    const nextAssigneeSearch = String(assignee || '').trim();
    const currentMessageSearch = String(messageSearch || '').trim();

    setSearch(nextThreadSearch);
    setMessageSearch(nextMessageSearch);
    setModeSearch(nextModeSearch);
    setStatusSearch(nextStatusSearch);
    setGroupSearch(nextGroupSearch);
    setAssigneeSearch(nextAssigneeSearch);

    if (nextMessageSearch !== currentMessageSearch) {
      await loadThreads({ messageKeyword: nextMessageSearch });
    }
  }, [assigneeSearch, groupSearch, loadThreads, messageSearch, modeSearch, search, statusSearch]);

  const searchToolbarChips = useMemo(() => {
    const resolvedGroupName =
      artistGroups.find((group) => String(group?.group_id || '') === String(groupSearch || ''))?.group_name ||
      String(groupSearch || '').trim();
    const resolvedStatusLabel =
      statusSearch === 'open' ? '公開中' : statusSearch === 'closed' ? 'クローズ' : '';
    return buildToolbarFilterChips(
      [
        {
          key: 'thread',
          label: 'チャット名',
          value: search,
          onRemove: () => applyToolbarSearchFilters({ thread: '' }),
        },
        {
          key: 'message',
          label: '本文',
          value: messageSearch,
          onRemove: () => applyToolbarSearchFilters({ message: '' }),
        },
        {
          key: 'mode',
          label: 'モード',
          value: modeSearch ? getThreadModeLabel(modeSearch) : '',
          onRemove: () => applyToolbarSearchFilters({ mode: '' }),
        },
        {
          key: 'status',
          label: 'ステータス',
          value: resolvedStatusLabel,
          onRemove: () => applyToolbarSearchFilters({ status: '' }),
        },
        {
          key: 'group',
          label: '連動グループ',
          value: resolvedGroupName,
          onRemove: () => applyToolbarSearchFilters({ groupId: '' }),
        },
        {
          key: 'assignee',
          label: '担当',
          value: assigneeSearch === 'mine' ? '自分' : '',
          onRemove: () => applyToolbarSearchFilters({ assignee: '' }),
        },
      ],
      {
        onClearAll: () => applyToolbarSearchFilters({
          thread: '',
          message: '',
          mode: '',
          status: '',
          groupId: '',
          assignee: '',
        }),
      }
    );
  }, [applyToolbarSearchFilters, artistGroups, assigneeSearch, groupSearch, messageSearch, modeSearch, search, statusSearch]);
  const assigneeQuickToggles = useMemo(
    () => [
      {
        key: 'mine',
        label: '担当',
        active: assigneeSearch === 'mine',
        onClick: () => setAssigneeSearch((prev) => (prev === 'mine' ? '' : 'mine')),
      },
    ],
    [assigneeSearch]
  );
  const openTargetSearchModal = useCallback(() => {
    setSearchDraft({
      thread: String(search || ''),
      message: String(messageSearch || ''),
      mode: String(modeSearch || ''),
      status: String(statusSearch || ''),
      groupId: String(groupSearch || ''),
    });
    if (!artistGroups.length && !loadingGroups) {
      loadArtistGroups();
    }
    setShowSearchModal(true);
  }, [
    artistGroups.length,
    groupSearch,
    loadArtistGroups,
    loadingGroups,
    messageSearch,
    modeSearch,
    search,
    statusSearch,
  ]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(GROUP_CHAT_ASSIGNEE_SEARCH_STORAGE_KEY);
      if (saved === 'mine') setAssigneeSearch('mine');
    } catch {}
  }, []);

  useEffect(() => {
    try {
      if (assigneeSearch) {
        window.localStorage.setItem(GROUP_CHAT_ASSIGNEE_SEARCH_STORAGE_KEY, assigneeSearch);
      } else {
        window.localStorage.removeItem(GROUP_CHAT_ASSIGNEE_SEARCH_STORAGE_KEY);
      }
    } catch {}
  }, [assigneeSearch]);

  const applyTargetSearchModal = useCallback(async () => {
    const nextThreadSearch = String(searchDraft.thread || '').trim();
    const nextMessageSearch = String(searchDraft.message || '').trim();
    const nextModeSearch = String(searchDraft.mode || '').trim();
    const nextStatusSearch = String(searchDraft.status || '').trim();
    const nextGroupSearch = String(searchDraft.groupId || '').trim();
    const currentMessageSearch = String(messageSearch || '').trim();
    setSearch(nextThreadSearch);
    setMessageSearch(nextMessageSearch);
    setModeSearch(nextModeSearch);
    setStatusSearch(nextStatusSearch);
    setGroupSearch(nextGroupSearch);
    setShowSearchModal(false);
    if (nextMessageSearch !== currentMessageSearch) {
      await loadThreads({ messageKeyword: nextMessageSearch });
    }
  }, [
    loadThreads,
    messageSearch,
    searchDraft.groupId,
    searchDraft.message,
    searchDraft.mode,
    searchDraft.status,
    searchDraft.thread,
  ]);

  const clearTargetSearchModal = useCallback(async () => {
    setSearchDraft({ thread: '', message: '', mode: '', status: '', groupId: '' });
    setSearch('');
    setMessageSearch('');
    setModeSearch('');
    setStatusSearch('');
    setGroupSearch('');
    setAssigneeSearch('');
    setShowSearchModal(false);
    if (String(messageSearch || '').trim()) {
      await loadThreads({ messageKeyword: '' });
    }
  }, [loadThreads, messageSearch]);

  const selectedAssignees = useMemo(() => {
    if (!Array.isArray(adminUsers) || !adminUsers.length) return [];
    const selectedSet = new Set(settingsAssigneeIds);
    return adminUsers.filter((admin) => selectedSet.has(Number.parseInt(admin?.id, 10)));
  }, [adminUsers, settingsAssigneeIds]);

  const selectedAssigneeNames = useMemo(
    () => selectedAssignees.map((admin) => admin.full_name || admin.username || `管理者#${admin.id}`),
    [selectedAssignees]
  );

  const filteredAdminUsers = useMemo(() => {
    const keyword = String(assigneeKeyword || '').trim().toLowerCase();
    if (!keyword) return adminUsers;
    return adminUsers.filter((admin) => {
      const name = `${admin?.full_name || ''} ${admin?.username || ''} ${admin?.position || ''}`.toLowerCase();
      return name.includes(keyword);
    });
  }, [adminUsers, assigneeKeyword]);

  const handleCreate = useCallback(async () => {
    const title = String(createTitle || '').trim();
    if (!title) {
      toastError('タイトルは必須です');
      return;
    }
    if (createMode === 'artist_group_linked' && !createGroupId) {
      toastError('連動グループを選択してください');
      return;
    }
    const createArtistIds =
      createMode === 'manual'
        ? createSelectedArtists
            .map((artist) => String(artist?.artist_id || '').trim())
            .filter(Boolean)
        : [];
    setSavingCreate(true);
    try {
      await createAdminGroupChatThread({
        title,
        mode: createMode,
        linkedArtistGroupId:
          createMode === 'artist_group_linked' ? Number(createGroupId) : null,
        artistIds: createArtistIds,
      });
      toastSuccess('グループチャットを作成しました');
      setCreateOpen(false);
      setCreateTitle('');
      setCreateMode('manual');
      setCreateGroupId('');
      setShowCreateArtistModal(false);
      setCreateArtistKeyword('');
      setCreateArtistAccountType('all');
      setCreateArtistFilterGroupId('');
      setCreateArtistFilterPart('');
      setCreateArtistCandidates([]);
      setCreateSelectedArtists([]);
      await loadThreads({ silent: true });
    } catch (error) {
      toastError(error?.message || 'グループチャット作成に失敗しました');
    } finally {
      setSavingCreate(false);
    }
  }, [
    createGroupId,
    createMode,
    createSelectedArtists,
    createTitle,
    loadThreads,
    toastError,
    toastSuccess,
  ]);

  const handleComposerSent = useCallback(async () => {
    if (!selectedThreadId) return;
    try {
      await Promise.all([
        loadMessages(selectedThreadId),
        loadThreadDetail(selectedThreadId),
        loadThreads({ silent: true }),
      ]);
      refreshPendingMentionTargets();
      setReplyDraft(null);
    } catch (error) {
      toastError(error?.message || 'メッセージ更新に失敗しました');
    }
  }, [loadMessages, loadThreadDetail, loadThreads, refreshPendingMentionTargets, selectedThreadId, toastError]);

  const imageItems = useMemo(
    () =>
      messages
        .filter((message) => message?.message_format === 'image' && message?.image_file_name)
        .map((message) => ({
          ...message,
          messageId: message.id,
          src: appendVariant(buildAssetUrl(MESSAGE_IMAGE_PATH, message.image_file_name), 'proxy'),
          fullSrc: appendVariant(buildAssetUrl(MESSAGE_IMAGE_PATH, message.image_file_name), 'raw'),
          downloadUrl: appendVariant(buildAssetUrl(MESSAGE_IMAGE_PATH, message.image_file_name), 'raw'),
          fileName: message.image_file_name,
        })),
    [messages]
  );
  const displayMessages = useMemo(
    () => buildImageGroupDisplayMessages(messages),
    [messages],
  );
  const selectedThreadKey = String(selectedThreadId || '').trim();
  const shouldShowEmptyMessages = Boolean(
    selectedThreadKey &&
      loadedMessagesThreadId === selectedThreadKey &&
      !loadingMessages &&
      !loadingOlderMessages &&
      !loadingNewerMessages &&
      displayMessages.length === 0
  );

  const openImageModal = useCallback(
    (messageOrItem) => {
      const messageId = typeof messageOrItem === 'object'
        ? (messageOrItem?.messageId || messageOrItem?.message_id || messageOrItem?.id)
        : messageOrItem;
      const index = imageItems.findIndex((item) => String(item.messageId) === String(messageId));
      if (index < 0) return;
      setImgModalIndex(index);
      setImgModalOpen(true);
    },
    [imageItems]
  );

  const closeImageModal = useCallback(() => {
    setImgModalOpen(false);
  }, []);

  const showPrevImage = useCallback(() => {
    if (!imageItems.length) return;
    setImgModalIndex((current) => (current - 1 + imageItems.length) % imageItems.length);
  }, [imageItems.length]);

  const showNextImage = useCallback(() => {
    if (!imageItems.length) return;
    setImgModalIndex((current) => (current + 1) % imageItems.length);
  }, [imageItems.length]);

  const closeMessageMenu = useCallback(() => {
    setMessageMenu(null);
  }, []);

  const closePartialCopyMode = useCallback(() => {
    setInlineCopyMessageId('');
  }, []);

  const canAnnouncementMessage = useCallback((message) => {
    const format = String(message?.message_format || '').trim();
    return format === 'text' || format === 'image' || format === 'file';
  }, []);

  const canUnsendMessage = useCallback(
    (message) =>
      canUnsendGroupChatMessage({
        message,
        viewerRole: 'admin',
        currentAdminId: loggedInAdminId,
      }),
    [loggedInAdminId]
  );

  const canReactMessage = useCallback((message) => {
    if (!message || !supportsChatMessageReaction(message)) return false;
    if (shouldExcludeAdminSenderForMessage(message)) return true;
    return !(
      String(message?.sender_type || '').trim().toLowerCase() === 'admin' &&
      String(message?.admin_user_id || '').trim() === String(loggedInAdminId || '').trim()
    );
  }, [loggedInAdminId]);

  const canInspectReadStatus = useCallback((message) => {
    if (!message) return false;
    return Boolean(String(message.id || '').trim());
  }, []);

  const canReplyMessage = useCallback((message) => {
    if (!message) return false;
    const senderType = String(message.sender_type || '').trim().toLowerCase();
    const format = String(message.message_format || '').trim().toLowerCase();
    return ['admin', 'artist'].includes(senderType) && ['text', 'image', 'file'].includes(format);
  }, []);

  const canCopyMessage = useCallback((message) => hasCopyableChatMessageText(message), []);

  const canOpenMessageMenu = useCallback(
    (message) =>
      canCopyMessage(message) ||
      canReplyMessage(message) ||
      canInspectReadStatus(message) ||
      canUnsendMessage(message) ||
      canAnnouncementMessage(message) ||
      canReactMessage(message),
    [canAnnouncementMessage, canCopyMessage, canInspectReadStatus, canReactMessage, canReplyMessage, canUnsendMessage]
  );

  const { menuRef: messageMenuRef, resolveMenuPosition } = useViewportBoundedMenuPosition({
    menu: messageMenu,
    setMenu: setMessageMenu,
    padding: 12,
    fallbackWidth: 248,
    fallbackHeight: 420,
  });

  const clearMessageLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMessageContextMenu = useCallback(
    (event, message) => {
      if (!canOpenMessageMenu(message)) return;
      event.preventDefault();
      const pos = resolveMenuPosition(event.clientX, event.clientY);
      setMessageMenu({ x: pos.x, y: pos.y, messageId: message.id, message });
    },
    [canOpenMessageMenu, resolveMenuPosition]
  );

  const handleMessageTouchStart = useCallback(
    (event, message) => {
      if (!isNarrow) return;
      if (!canOpenMessageMenu(message)) return;
      clearMessageLongPress();
      const touch = event.touches?.[0];
      if (!touch) return;
      const { clientX, clientY } = touch;
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        const pos = resolveMenuPosition(clientX, clientY);
        setMessageMenu({ x: pos.x, y: pos.y, messageId: message.id, message });
      }, 550);
    },
    [canOpenMessageMenu, clearMessageLongPress, isNarrow, resolveMenuPosition]
  );

  const handleUnsendMessage = useCallback(async () => {
    const messageId = messageMenu?.messageId ? String(messageMenu.messageId) : '';
    if (!selectedThreadId || !messageId || unsendingMessageId) return;
    setUnsendingMessageId(messageId);
    try {
      const data = await deleteAdminGroupChatMessage(String(selectedThreadId), messageId);
      setMessageMenu(null);
      const deletedIds = Array.isArray(data?.messageIds) && data.messageIds.length
        ? data.messageIds.map((id) => String(id))
        : [messageId];
      setMessages((prev) => removeMessages(prev, deletedIds));
      await Promise.all([loadThreadDetail(selectedThreadId), loadThreads({ silent: true })]);
      toastSuccess('メッセージを取り消しました');
    } catch (error) {
      toastError(error?.message || '送信取り消しに失敗しました');
    } finally {
      setUnsendingMessageId('');
    }
  }, [
    loadThreadDetail,
    loadThreads,
    messageMenu?.messageId,
    selectedThreadId,
    toastError,
    toastSuccess,
    unsendingMessageId,
  ]);

  const handleToggleAnnouncementMessage = useCallback(async () => {
    const messageId = messageMenu?.messageId ? String(messageMenu.messageId) : '';
    if (!messageId || announcementActionMessageId) return;
    setMessageMenu(null);
    try {
      if (isAnnouncedMessage(messageId)) {
        await unpinAnnouncement(messageId);
      } else {
        await pinAnnouncement(messageId);
      }
    } catch (_) {}
  }, [announcementActionMessageId, isAnnouncedMessage, messageMenu?.messageId, pinAnnouncement, unpinAnnouncement]);

  const handleMessageMenuReactionSelect = useCallback(async (messageId, reactionType) => {
    setMessageMenu(null);
    try {
      await toggleReaction(messageId, reactionType);
    } catch (_) {}
  }, [toggleReaction]);

  const handleCopyMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    setMessageMenu(null);
    if (!copyText) {
      toastError('コピーできる本文がありません。');
      return;
    }
    try {
      await writeTextToClipboard(copyText);
      toastSuccess('メッセージをコピーしました');
    } catch (error) {
      toastError(error?.message || 'コピーに失敗しました');
    }
  }, [messageMenu, toastError, toastSuccess]);

  const handleOpenInlinePartialCopy = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    setMessageMenu(null);
    if (!copyText) {
      toastError('コピーできる本文がありません。');
      return;
    }
    const nextMessageId = String(target.id || '').trim();
    setInlineCopyMessageId((current) => (current === nextMessageId ? '' : nextMessageId));
  }, [messageMenu, toastError]);

  const handleCopySelectionText = useCallback(async (selectedText) => {
    if (!String(selectedText || '').trim()) {
      toastError('コピーしたい範囲を選択してください。');
      return;
    }
    try {
      await writeTextToClipboard(selectedText);
      toastSuccess('選択範囲をコピーしました');
    } catch (error) {
      toastError(error?.message || 'コピーに失敗しました');
    }
  }, [toastError, toastSuccess]);

  const renderPartialCopyContent = useCallback((message, contentNode, isSelfMessage = false) => {
    const normalizedMessageId = String(message?.id || '').trim();
    if (!normalizedMessageId || !canCopyMessage(message)) return contentNode;
    return (
      <ChatMessageSelectableCopyArea
        active={inlineCopyMessageId === normalizedMessageId}
        messageId={normalizedMessageId}
        copyButtonSide={isSelfMessage ? 'left' : 'right'}
        onDeactivate={closePartialCopyMode}
        onCopySelection={handleCopySelectionText}
      >
        {contentNode}
      </ChatMessageSelectableCopyArea>
    );
  }, [canCopyMessage, closePartialCopyMode, handleCopySelectionText, inlineCopyMessageId]);

  const handleOpenReadStatusDetail = useCallback(async () => {
    const messageId = messageMenu?.messageId ? String(messageMenu.messageId) : '';
    if (!messageId) return;
    setMessageMenu(null);
    await openReadStatusDetail(messageId);
  }, [messageMenu?.messageId, openReadStatusDetail]);

  const buildReplyDraftFromMessage = useCallback((message) => {
    if (!message) return null;
    const format = String(message.message_format || '').trim().toLowerCase();
    return {
      messageId: String(message.id || '').trim(),
      senderName:
        String(message.sender_type || '').trim().toLowerCase() === 'admin'
          ? formatAdminDisplayName({
              adminDisplayName: message.admin_display_name || '',
              fullName: message.admin_full_name || '',
              username: message.admin_username || '',
              role: message.admin_role || '',
              isExternalAccount: message.admin_is_external_account === true,
              externalAffiliationName: message.admin_external_affiliation_name || '',
              useAsciiParentheses: true,
            })
          : String(message.artist_name || message.artist_name_en || 'アーティスト').trim(),
      type: format,
      previewText: format === 'text' ? String(message.content || '').trim() : '',
      fileName:
        format === 'file'
          ? String(message.original_name || message.file_file_name || '').trim()
          : '',
    };
  }, []);

  const handleSelectReplyMessage = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    setMessageMenu(null);
    setReplyDraft(buildReplyDraftFromMessage(target));
  }, [buildReplyDraftFromMessage, messageMenu]);

  const handleSelectAnnouncementMessage = useCallback(
    async (messageId) => {
      const normalizedMessageId = String(messageId || '').trim();
      if (!selectedThreadId || !normalizedMessageId) return;
      setIsAnnouncementListOpen(false);
      await loadMessages(selectedThreadId, { anchorMessageId: normalizedMessageId });
    },
    [loadMessages, selectedThreadId, setIsAnnouncementListOpen],
  );

  const handleJumpToReplyMessage = useCallback(async (reply) => {
    const normalizedMessageId = String(reply?.messageId || '').trim();
    if (!selectedThreadId || !normalizedMessageId) return;
    if (reply?.isUnavailable) {
      toastError('返信元メッセージが見つかりません。');
      return;
    }
    await loadMessages(selectedThreadId, { anchorMessageId: normalizedMessageId });
  }, [loadMessages, selectedThreadId, toastError]);

  const handleDropFiles = useCallback(
    async (files = [], { rejectedCount = 0 } = {}) => {
      if (!selectedThreadId) {
        toastError('チャットを選択してください');
        return;
      }
      if (!selectedThread || selectedThread.status !== 'open') {
        toastError('クローズ中のため送信できません');
        return;
      }
      const incoming = Array.from(files || []).filter(Boolean);
      if (!incoming.length) return;

      const oversized = incoming.filter(
        (file) => typeof file?.size === 'number' && file.size > GROUP_CHAT_MAX_FILE_BYTES
      ).length;
      const valid = incoming.filter(
        (file) => typeof file?.size !== 'number' || file.size <= GROUP_CHAT_MAX_FILE_BYTES
      );
      if (!valid.length) {
        toastError('100MBを超えるファイルは送信できません');
        return;
      }

      const images = valid.filter(isImageFile).slice(0, 5);
      const imageOverflow = Math.max(0, valid.filter(isImageFile).length - images.length);
      const filesOnly = valid.filter((file) => !isImageFile(file));
      const fileToSend = filesOnly[0] || null;
      const fileOverflow = Math.max(0, filesOnly.length - (fileToSend ? 1 : 0));

      setUploadingDropFiles(true);
      try {
        if (images.length > 0) {
          await sendImages({
            mode: 'admin',
            scope: 'group',
            groupThreadId: selectedThreadId,
            files: images,
            senderId: loggedInAdminId,
            replyToMessageId: replyDraft?.messageId || '',
          });
        }
        if (fileToSend) {
          await sendFile({
            mode: 'admin',
            scope: 'group',
            groupThreadId: selectedThreadId,
            file: fileToSend,
            senderId: loggedInAdminId,
            replyToMessageId: replyDraft?.messageId || '',
          });
        }
        await handleComposerSent();
        toastSuccess('ファイルを送信しました');
        if (rejectedCount || oversized || imageOverflow || fileOverflow) {
          toastError('一部のファイルは制限により送信されませんでした');
        }
      } catch (error) {
        toastError(error?.message || 'ファイル送信に失敗しました');
      } finally {
        setUploadingDropFiles(false);
      }
    },
    [
      handleComposerSent,
      loggedInAdminId,
      replyDraft,
      selectedThread,
      selectedThreadId,
      toastError,
      toastSuccess,
    ]
  );

  const { isDragActive, overlayRect } = useChatDragDrop({
    areaRef: messagesContainerRef,
    highlightRef: chatDropScopeRef,
    enabled: Boolean(selectedThreadId && selectedThread?.status === 'open' && !uploadingDropFiles),
    onDrop: handleDropFiles,
    onInvalid: (message) => toastError(message || 'この操作は利用できません'),
    maxBytes: GROUP_CHAT_MAX_FILE_BYTES,
  });

  useEffect(() => {
    if (!messageMenu) return undefined;
    const handlePointerDown = () => setMessageMenu(null);
    const target = messagesContainerRef.current;
    window.addEventListener('click', handlePointerDown);
    target?.addEventListener('scroll', handlePointerDown, { passive: true });
    return () => {
      window.removeEventListener('click', handlePointerDown);
      target?.removeEventListener('scroll', handlePointerDown);
    };
  }, [messageMenu]);

  useEffect(() => () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const toggleStatusLabel = useMemo(() => {
    if (!selectedThread) return '';
    return selectedThread.status === 'open' ? 'クローズ' : '再開';
  }, [selectedThread]);

  const currentStatusLabel = useMemo(() => {
    if (!selectedThread) return '';
    return selectedThread.status === 'open' ? '公開中' : '閉鎖中';
  }, [selectedThread]);

  const currentStatusIconClass = useMemo(() => {
    if (!selectedThread) return 'fa-lock';
    return selectedThread.status === 'open' ? 'fa-lock-open' : 'fa-lock';
  }, [selectedThread]);

  const handleToggleStatus = useCallback(async () => {
    if (!selectedThreadId || !selectedThread || togglingStatus) return;
    setTogglingStatus(true);
    try {
      if (selectedThread.status === 'open') {
        await closeAdminGroupChatThread(String(selectedThreadId));
      } else {
        await openAdminGroupChatThread(String(selectedThreadId));
      }
      await Promise.all([loadThreadDetail(selectedThreadId), loadThreads({ silent: true })]);
      toastSuccess('チャット状態を更新しました');
    } catch (error) {
      toastError(error?.message || '状態更新に失敗しました');
    } finally {
      setTogglingStatus(false);
    }
  }, [
    loadThreadDetail,
    loadThreads,
    selectedThread,
    selectedThreadId,
    toastError,
    toastSuccess,
    togglingStatus,
  ]);

  const openSettingsModal = useCallback(
    async (threadInfo = null, { selectOnOpen = true } = {}) => {
      const fallbackThread = threadInfo || selectedThread || null;
      const targetThreadId =
        String(fallbackThread?.thread_id || '').trim() || String(selectedThreadId || '').trim();
      if (!targetThreadId) return;

      if (selectOnOpen && String(selectedThreadId || '') !== targetThreadId) {
        setSelectedThreadId(targetThreadId);
      }

      settingsThreadIdRef.current = targetThreadId;
      const initialThread =
        fallbackThread && String(fallbackThread?.thread_id || '') === targetThreadId
          ? fallbackThread
          : null;
      if (initialThread) {
        setSettingsThread(initialThread);
        setThreadMuted(initialThread?.admin_muted == null ? true : !!initialThread.admin_muted);
      } else {
        setSettingsThread(null);
      }
      setShowSettingsModal(true);

      try {
        const detailPromise = selectOnOpen
          ? loadThreadDetail(targetThreadId)
          : loadThreadDetail(targetThreadId, { updateSelected: false });

        const [detail] = await Promise.all([
          detailPromise,
          loadAdminUsers(),
          loadMuteSetting(targetThreadId),
        ]);

        if (detail && String(detail?.thread_id || '') === targetThreadId) {
          setSettingsThread(detail);
          setThreadMuted(detail?.admin_muted == null ? true : !!detail.admin_muted);
        }
      } catch (_) {}
    },
    [loadAdminUsers, loadMuteSetting, loadThreadDetail, selectedThread, selectedThreadId]
  );

  const closeSettingsModal = useCallback(() => {
    setShowSettingsModal(false);
    setSettingsThread(null);
    settingsThreadIdRef.current = '';
    setShowAssigneeModal(false);
    setSavingSettings(false);
    setDeletingThread(false);
    setSavingMute(false);
    setUploadingIcon(false);
    setDeletingIcon(false);
    setShowIconCropModal(false);
    setIconCropImageSrc('');
    if (settingsIconFileInputRef.current) {
      settingsIconFileInputRef.current.value = '';
    }
  }, []);

  const clearSidebarLongPress = useCallback(() => {
    if (sidebarPressRef.current.timer) {
      clearTimeout(sidebarPressRef.current.timer);
      sidebarPressRef.current.timer = null;
    }
  }, []);

  const clearSidebarContextMenuBlock = useCallback(() => {
    if (sidebarPressRef.current.contextMenuTimer) {
      clearTimeout(sidebarPressRef.current.contextMenuTimer);
      sidebarPressRef.current.contextMenuTimer = null;
    }
    sidebarPressRef.current.suppressContextMenu = false;
  }, []);

  const blockSidebarContextMenu = useCallback(() => {
    clearSidebarContextMenuBlock();
    sidebarPressRef.current.suppressContextMenu = true;
    sidebarPressRef.current.contextMenuTimer = setTimeout(() => {
      sidebarPressRef.current.suppressContextMenu = false;
      sidebarPressRef.current.contextMenuTimer = null;
    }, 800);
  }, [clearSidebarContextMenuBlock]);

  useEffect(() => {
    const handleContextMenu = (event) => {
      if (!sidebarPressRef.current.suppressContextMenu) return;
      event.preventDefault();
      event.stopPropagation();
      clearSidebarContextMenuBlock();
    };
    window.addEventListener('contextmenu', handleContextMenu, true);
    return () => {
      window.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [clearSidebarContextMenuBlock]);

  const handleSidebarTouchStart = useCallback(
    (threadInfo) => {
      clearSidebarLongPress();
      sidebarPressRef.current.timer = setTimeout(() => {
        sidebarPressRef.current.timer = null;
        sidebarPressRef.current.suppressClick = true;
        blockSidebarContextMenu();
        openSettingsModal(threadInfo, { selectOnOpen: false });
      }, 1000);
    },
    [blockSidebarContextMenu, clearSidebarLongPress, openSettingsModal]
  );

  const handleIconFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setIconCropImageSrc(String(reader.result || ''));
      setShowIconCropModal(true);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleSaveCroppedIcon = useCallback(
    async (croppedImage) => {
      const targetThreadId = String(settingsThreadIdRef.current || selectedThreadId || '').trim();
      if (!targetThreadId || !croppedImage || uploadingIcon) return;
      setUploadingIcon(true);
      try {
        await uploadAdminGroupChatThreadIcon(targetThreadId, { icon: croppedImage });

        const [detail] = await Promise.all([
          loadThreadDetail(targetThreadId, {
            updateSelected: String(selectedThreadId || '') === targetThreadId,
          }),
          loadThreads({ silent: true }),
        ]);
        if (detail && String(detail?.thread_id || '') === targetThreadId) {
          setSettingsThread(detail);
        }
        toastSuccess('グループチャットアイコンを更新しました');
        setShowIconCropModal(false);
        setIconCropImageSrc('');
        if (settingsIconFileInputRef.current) {
          settingsIconFileInputRef.current.value = '';
        }
      } catch (error) {
        toastError(error?.message || 'グループチャットアイコンの更新に失敗しました');
      } finally {
        setUploadingIcon(false);
      }
    },
    [loadThreadDetail, loadThreads, selectedThreadId, toastError, toastSuccess, uploadingIcon]
  );

  const handleDeleteIcon = useCallback(async () => {
    const targetThreadId = String(settingsThreadIdRef.current || selectedThreadId || '').trim();
    if (!targetThreadId || deletingIcon || uploadingIcon) return;
    setDeletingIcon(true);
    try {
      await deleteAdminGroupChatThreadIcon(targetThreadId);
      const [detail] = await Promise.all([
        loadThreadDetail(targetThreadId, {
          updateSelected: String(selectedThreadId || '') === targetThreadId,
        }),
        loadThreads({ silent: true }),
      ]);
      if (detail && String(detail?.thread_id || '') === targetThreadId) {
        setSettingsThread(detail);
      }
      toastSuccess('グループチャットアイコンを削除しました');
      if (settingsIconFileInputRef.current) {
        settingsIconFileInputRef.current.value = '';
      }
    } catch (error) {
      toastError(error?.message || 'グループチャットアイコンの削除に失敗しました');
    } finally {
      setDeletingIcon(false);
    }
  }, [
    deletingIcon,
    loadThreadDetail,
    loadThreads,
    selectedThreadId,
    toastError,
    toastSuccess,
    uploadingIcon,
  ]);

  const openAssigneeModal = useCallback(() => {
    setAssigneeDraftIds(settingsAssigneeIds);
    setAssigneeKeyword('');
    setShowAssigneeModal(true);
  }, [settingsAssigneeIds]);

  const closeAssigneeModal = useCallback(() => {
    setShowAssigneeModal(false);
    setAssigneeDraftIds(settingsAssigneeIds);
    setAssigneeKeyword('');
  }, [settingsAssigneeIds]);

  const applyAssigneeModal = useCallback(() => {
    setSettingsAssigneeIds(Array.from(new Set(assigneeDraftIds)));
    setShowAssigneeModal(false);
  }, [assigneeDraftIds]);

  const toggleAssigneeDraft = useCallback((adminId) => {
    setAssigneeDraftIds((current) =>
      current.includes(adminId)
        ? current.filter((value) => value !== adminId)
        : [...current, adminId]
    );
  }, []);

  const handleSaveSettings = useCallback(async () => {
    const targetThreadId = String(settingsThreadIdRef.current || selectedThreadId || '').trim();
    if (!targetThreadId || !settingsThread || savingSettings) return;
    const title = String(settingsTitle || '').trim();
    if (!title) {
      toastError('タイトルは必須です');
      return;
    }
    setSavingSettings(true);
    try {
      await updateAdminGroupChatThreadSettings(targetThreadId, {
        title,
        assigneeAdminIds: settingsAssigneeIds,
      });
      const [detail] = await Promise.all([
        loadThreadDetail(targetThreadId, {
          updateSelected: String(selectedThreadId || '') === targetThreadId,
        }),
        loadThreads({ silent: true }),
        showMembersModal && String(selectedThreadId || '') === targetThreadId
          ? loadMembers(targetThreadId)
          : Promise.resolve(),
      ]);
      if (detail && String(detail?.thread_id || '') === targetThreadId) {
        setSettingsThread(detail);
      }
      toastSuccess('チャット設定を更新しました');
      setShowSettingsModal(false);
      settingsThreadIdRef.current = '';
      setSettingsThread(null);
    } catch (error) {
      toastError(error?.message || 'チャット設定の更新に失敗しました');
    } finally {
      setSavingSettings(false);
    }
  }, [
    loadMembers,
    loadThreadDetail,
    loadThreads,
    savingSettings,
    selectedThreadId,
    settingsThread,
    settingsAssigneeIds,
    settingsTitle,
    showMembersModal,
    toastError,
    toastSuccess,
  ]);

  const handleDeleteThread = useCallback(async () => {
    const targetThreadId = String(settingsThreadIdRef.current || selectedThreadId || '').trim();
    if (!targetThreadId || deletingThread || savingSettings || loadingThreadDetail) return;

    const confirmed = (await confirmDialog({ message:
      'このグループチャットを完全に削除します。メッセージ、メンバー、設定、添付ファイルを含む関連データは元に戻せません。削除しますか？'
    , danger: true }));
    if (!confirmed) return;

    setDeletingThread(true);
    try {
      await deleteAdminGroupChatThread(targetThreadId);

      const isSelectedThread = String(selectedThreadId || '') === targetThreadId;
      if (isSelectedThread) {
        setSelectedThreadId('');
        setSelectedThread(null);
        setMessages([]);
        setLoadedMessagesThreadId('');
        setMembersPayload(null);
        setShowMembersModal(false);
        setShowMembersAddModal(false);
        setMessageMenu(null);
        if (isNarrow) {
          setMobileView('list');
        }
      }

      closeSettingsModal();
      await loadThreads({ silent: true });
      toastSuccess('グループチャットを削除しました');
    } catch (error) {
      toastError(error?.message || 'グループチャットの削除に失敗しました');
    } finally {
      setDeletingThread(false);
    }
  }, [confirmDialog,
    closeSettingsModal,
    deletingThread,
    isNarrow,
    loadThreads,
    loadingThreadDetail,
    savingSettings,
    selectedThreadId,
    toastError,
    toastSuccess,
  ]);

  // notifyOn = スイッチON（通知オン）。muted はその反転。
  // 楽観反映/巻き戻しは DsSwitch が担当（失敗時 throw）。
  const handleToggleMute = useCallback(async (notifyOn) => {
    const targetThreadId = String(settingsThreadIdRef.current || selectedThreadId || '').trim();
    if (!targetThreadId) throw new Error('no-thread');
    const nextMuted = !notifyOn;
    setSavingMute(true);
    try {
      await updateAdminGroupChatNotifyMute({
        threadId: targetThreadId,
        muted: nextMuted,
      });
      setThreadMuted(nextMuted);
      applyThreadMutedState(targetThreadId, nextMuted);
      toastSuccess(nextMuted ? 'このチャットの通知をオフにしました' : 'このチャットの通知をオンにしました');
    } catch (error) {
      toastError(error?.message || '通知設定の更新に失敗しました');
      throw error; // DsSwitch が表示を巻き戻し
    } finally {
      setSavingMute(false);
    }
  }, [
    applyThreadMutedState,
    selectedThreadId,
    toastError,
    toastSuccess,
  ]);

  const openMembersModal = useCallback(async () => {
    if (!selectedThreadId) return;
    setShowMembersModal(true);
    setShowMembersAddModal(false);
    setCandidateSelection([]);
    setArtistKeyword('');
    setMemberAddAccountType('all');
    setMemberAddFilterGroupId('');
    setMemberAddFilterPart('');
    setArtistCandidates([]);
    await loadMembers(selectedThreadId);
  }, [loadMembers, selectedThreadId]);

  const openMembersAddModal = useCallback(() => {
    setShowMembersAddModal(true);
    setCandidateSelection([]);
    setArtistKeyword('');
    setMemberAddAccountType('all');
    setMemberAddFilterGroupId('');
    setMemberAddFilterPart('');
    setArtistCandidates([]);
  }, []);

  const closeMembersAddModal = useCallback(() => {
    setShowMembersAddModal(false);
    setCandidateSelection([]);
    setArtistKeyword('');
    setMemberAddAccountType('all');
    setMemberAddFilterGroupId('');
    setMemberAddFilterPart('');
    setArtistCandidates([]);
    setAddingMembers(false);
  }, []);

  const closeMembersModal = useCallback(() => {
    setShowMembersModal(false);
    setShowMembersAddModal(false);
    setCandidateSelection([]);
    setArtistKeyword('');
    setMemberAddAccountType('all');
    setMemberAddFilterGroupId('');
    setMemberAddFilterPart('');
    setArtistCandidates([]);
    setAddingMembers(false);
    setRemovingArtistId('');
  }, []);

  const {
    manualCandidateRows,
    resolveArtistShortPart, buildArtistProfileData, buildAdminProfileData, formatAdminNameForMemberPeople, adminInitialForMemberPeople,
    adminAvatarStyleForMemberPeople, artistAvatarStyleForMemberPeople, artistAvatarInitialForMemberPeople,
    groupedMemberArtistSections, groupedSidePanelArtists,
    mentionCandidates, openMessageAvatarProfile, handleMentionClick, toggleCandidate, addAllMemberCandidates,
    handleAddMembers, handleRemoveMember, createSelectedArtistIdSet, removeCreateArtist,
    toggleCreateArtist, addAllCreateCandidates,
  } = useAdminGroupChatMemberState({
    addingMembers, artistCandidates, candidateSelection, createArtistCandidates, createSelectedArtists, loadMembers,
    loadThreadDetail, loadThreads, loggedInAdminId, membersPayload, openAdminProfile, openArtistProfile,
    openProfileTarget, parts, removingArtistId, selectedThread, selectedThreadId, setAddingMembers,
    setCandidateSelection, setCreateSelectedArtists, setRemovingArtistId, setShowMembersAddModal, setShowMembersModal, toastError,
    toastSuccess,
  });
  const settingsModalThread = settingsThread || selectedThread;
  const rootClasses = [
    'admin-message-container',
    'app-main',
    'app-main--force',
    'admin-group-chat-container',
    isNarrow ? 'is-mobile' : '',
    isNarrow && mobileView === 'chat' ? 'is-mobile-chat' : '',
    isNarrow && mobileView === 'list' ? 'is-mobile-list' : '',
  ]
    .filter(Boolean)
    .join(' ');

    return (
    <AdminGroupChatPageView
      context={{
        activeAnchorMessageId, activeSearchFilterCount, addAllCreateCandidates, addAllMemberCandidates, addingMembers, adminArtistChatTone, adminAvatarStyleForMemberPeople, adminInitialForMemberPeople, adminMessageCount, adminUsers,
        announcementActionMessageId, announcementCount, announcements, applyAssigneeModal, applyTargetSearchModal, artistAvatarInitialForMemberPeople, artistAvatarStyleForMemberPeople, artistGroups,
        artistKeyword, assigneeDraftIds, assigneeKeyword, assigneeQuickToggles, buildAdminProfileData, buildArtistProfileData, canAnnouncementMessage, canCopyMessage,
        canInspectReadStatus, canManageGroupNotes, canReactMessage, canReplyMessage, canSwitchToPersonalChat: canUsePersonalChat, canUnsendMessage, canViewGroupNotes, candidateSelection, chatDropScopeRef,
        clearMessageLongPress, clearSidebarContextMenuBlock, clearSidebarLongPress, clearTargetSearchModal, closeAssigneeModal, closeImageModal, closeMembersAddModal, closeMembersModal,
        closeMessageMenu, closeProfileModal, closeReactionDetail, closeReadStatusDetail, closeSettingsModal, createArtistAccountType, createArtistCandidates, createArtistFilterGroupId,
        createArtistFilterPart, createArtistKeyword, createGroupId, createMode, createOpen, createSelectedArtistIdSet, createSelectedArtists, createTitle,
        currentStatusIconClass, currentStatusLabel, deletingIcon, deletingThread, displayMessages, filteredAdminUsers, filteredThreads, followupBadgeCount,
        followupRefreshKey, followupSummary, formatAdminNameForMemberPeople, goBackMobile, groupedMemberArtistSections, groupedSidePanelArtists, handleAddMembers, handleComposerSent,
        handleCopyMessage, handleCreate, handleDeleteIcon, handleDeleteThread, handleIconFileChange, handleJumpToReplyMessage, handleMentionClick, handleMessageContextMenu,
        handleMessageMenuReactionSelect, handleMessageTouchStart, handleOpenAiTask, handleOpenInlinePartialCopy, handleOpenReadStatusDetail, handleRemoveMember, handleSaveCroppedIcon, handleSaveSettings,
        handleOpenPersonalChatFromTargetList, handleSelectAnnouncementMessage, handleSelectReplyMessage, handleSidebarTouchStart, handleToggleAnnouncementMessage, handleToggleMute, handleToggleStatus, handleUnsendMessage, hasMoreMessages, iconCropImageSrc,
        imageItems, imgModalIndex, imgModalOpen, inlineCopyMessageId, isAiTargetSelected, isAnnouncedMessage, isAnnouncementListOpen, isDragActive,
        isGroupChatAreaOpen, isNarrow, isReactionDetailOpen, isReactionPending, isReadStatusDetailOpen, isSidePanelLayoutOpen, isSidePanelOpen, latestAnnouncement,
        loadMessages, loadThreadDetail, loadingAdminUsers, loadingArtistCandidates, loadingCreateArtistCandidates, loadingGroups, loadingMembers, loadingMentionResolutionTargetKey,
        loadingMute, loadingOlderMessages, loadingParts, loadingThreadDetail, loggedInAdminId, manualCandidateRows, memberAddAccountType, memberAddFilterGroupId,
        memberAddFilterPart, membersPayload, mentionCandidates, mentionResolutionModalProps, messageMenu, messageMenuRef, messageSearch, messages,
        messagesContainerRef, mobileListPrefRef, mobileView, noteCount, openAdminProfile, openAiInbox, openArtistProfile, openAssigneeModal,
        openImageModal, openMembersAddModal, openMembersModal, openMentionResolution, openMessageAvatarProfile, openReactionDetail, openSettingsModal, openTargetSearchModal,
        overlayRect, parts, pendingAnchorRef, pendingMentionTargets, profileTarget, reactionDetailGroups, reactionDetailLoading, readStatusDetailLoading,
        readStatusDetailReaders, refreshNoteCount, removeCreateArtist, removingArtistId, renderPartialCopyContent, replyDraft, resolveArtistShortPart, resolveMentionTarget,
        resolvingMentionTargetId, rootClasses, savingCreate, savingMute, savingSettings, searchDraft, searchSummaryLabel, searchToolbarChips,
        selectedAssigneeNames, selectedAssignees, selectedThread, selectedThreadId, setArtistKeyword, setAssigneeKeyword, setCandidateSelection, setCreateArtistAccountType,
        setCreateArtistFilterGroupId, setCreateArtistFilterPart, setCreateArtistKeyword, setCreateGroupId, setCreateMode, setCreateOpen, setCreateSelectedArtists, setCreateTitle,
        setIconCropImageSrc, setIsAiTargetSelected, setIsAnnouncementListOpen, setIsSidePanelOpen, setMemberAddAccountType, setMemberAddFilterGroupId, setMemberAddFilterPart, setMobileView,
        setReplyDraft, setSearchDraft, setSelectedThreadId, setSettingsTitle, setShowCreateArtistModal, setShowIconCropModal, setShowNotes, setShowSearchModal,
        settingsIconFileInputRef, settingsModalThread, settingsTitle, shouldShowEmptyMessages, shouldShowSidePanel, shouldShowThreadSkeleton, shouldSuppressThreadUnread, showAssigneeModal,
        showCreateArtistModal, showDelayedInitialLoading, showIconCropModal, showMembersAddModal, showMembersModal, showNextImage, showNotes, showPrevImage,
        showSearchModal, showSettingsModal, sidebarPressRef, threadMuted, toggleAssigneeDraft, toggleCandidate, toggleCreateArtist, toggleReaction,
        toastError, toastSuccess, toggleStatusLabel, togglingStatus, unpinAnnouncement, unsendingMessageId, uploadingIcon,
      }}
    />
  );
}
